import type {
  IGetTableInfoDto,
  ITableColumnClassDto,
} from '@dbl/services/dataSource';
import {
  convertTableColumnToColumnRawInfo,
  getPreviewSQL,
  getTableInfoList,
} from '@dbl/services/dataSource';
import type { TEnrichedTableColumn } from '../types/tableColumnEnriched';
/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/consistent-type-assertions */
/* eslint-disable no-param-reassign */
import {
  calculateAnalysisChart,
  cancelAnalysis,
  getChartDetail,
  saveChart,
} from '@dbl/services/chart';
import type { IColumnInfo, IColumnRawInfo } from '@dbl/core/dataSource';
import {
  Analysis,
  AnalysisFilters,
  AnalysisPackageInfo,
  Condition,
  convertCustomColumnToColumnRawInfo,
  CustomColumn,
  DimensionMapInfo,
  FiltersItem,
  GroupBy,
  MetricsItem,
  OrderByItem,
  UpdateColumnInfo,
} from './interface';
import { ChartCalculateMode, type TChartCalculateMode } from '@dbl/core/chartEditor';
import moment from 'moment';
import {v4 as uid} from 'uuid';
import * as crypto from 'crypto-js';
import {
  BaseModel,
  createFilterCompares,
  createFiltersItem,
  createGroupBy,
  createMetricsItem,
  getRequestUniqueId,
} from '@/pages/datalab/components/SortableDrag/baseModel';
import { DateGranularity } from '@dbl/core/date';
import {DBColumnType, DragState, ErrorDrag} from '@/pages/datalab/components/SortableDrag/typeDefine';
import {includeOperation} from '@/pages/datalab/components/SortableDrag/utils';
import type { INumberPackage } from '@dbl/services/numberPackage';
import { getNumberPackageDetail } from '@dbl/services/numberPackage';
import {
  checkNullString,
  convertToString,
  formatStatisticsData,
  getNullTextOfFilter,
  hasEmptyProperty
} from '@/pages/datalab/utils/utils';
import {ITableJoinRelation} from './components/HeaderData/interface';
import {aggregateObj, FuncNameMap} from './components/AnalysisLineChart/constants';
import { ChartType, type TChartType } from '@dbl/core/chart';
import { SwitchChartTypes } from '@dbl/core/chartEditor';
// 自定义字段改名 → 迁移图表设置（见 updateCustomColumn 内注释）
import {
  applyCustomColumnDeleteOnQB,
  applyCustomColumnRenameOnQB,
  applyDatasetFieldSortResetIfUnresolved,
  applyDatasetFieldSortResetOnCustomColumnDelete,
} from './components/ChartSettingsV2/utils/chartSettingsBridge';
import {
  buildRemovalChangeSet,
  collectRemovedSnapshotsForCustomColumn,
  pruneSaveColumnInfosOnCustomColumnDelete,
  pruneSaveColumnInfosOnDatasetFieldSortCustomDelete,
  pruneSaveColumnInfosOnDatasetFieldSortUnresolved,
} from './components/ChartSettingsV2/utils/customColumnConfigDelete';
import { detectDateDimensionsInList } from './components/ChartSettingsV2/manifest/dimensionDateUtils';
import { migrateSaveColumnInfosOnCustomColumnRename } from './components/ChartSettingsV2/utils/customColumnConfigRename';
import {camelCase, isEmpty, omit} from 'lodash-es';
import {checkAllConditions, checkRawColumnModeConditions} from '../utils';
import { getReportDimensionInfo } from '@dbl/services/filter';
import {
  FieldTypeDefaultAccuracy,
  getDefaultNumberUnit,
  getOldChartDisplayConfig,
} from '@dbl/core/chartEditor';
import { ITableAnalyticsData } from '@customComp/AliTableAnalytics/types';
import {allowChartTypeShowUserFilter} from "@dbl/core/global";
import { DATALAB_COMPARE_USER_FILTERS } from '@/pages/datalab/components/UserFilter/FilterPanel/constant';
import { mapPercentAxisWireToChartData } from './components/AnalysisLineChart/mapPercentAxisWireToChartData';

const service = {
  saveChart,
  getPreviewSQL,
  calculateAnalysisChart,
  cancelAnalysis,
  getChartDetail,
  getTableInfoList,
};

export const namespace = 'Analysis/MultiAnalysis';

const analysisModel = new BaseModel(namespace);

const createAnalysisCacheKey = (taskId?: string): string =>
  `${taskId || 'analysis_task'}_${Date.now()}`;


/**
 * 已保存图表进页时，在 getTableInfo 阶段与 save_column_infos 同步写入任务字段，
 * 避免 ChartSettingsBaseInit 在 chartType 仍为默认 Table 时误初始化 BASE。
 */
function buildTaskRestoreFields(
  taskRsp: Record<string, unknown> | undefined,
  taskId?: number | string,
): Record<string, unknown> {
  if (!taskRsp) {
    return {};
  }
  return {
    task_id:
      taskId != null && taskId !== ''
        ? String(taskId)
        : String(taskRsp.task_id ?? ''),
    chartType: taskRsp.chart_type,
    statisticalMetrics: taskRsp.statistical_metrics as string | undefined,
  };
}

/**
 * 从 filter.custom_columns 归一化自定义字段。
 * 进页时须在 setTableInfo（B1 prune）之前写入 state：否则 customColumns 仍空，
 * 会把「字段排序引用自定义字段」误判不可解析并清空 save_column_infos。
 */
function normalizeCustomColumnsFromFilter(
  customColumnsRaw: CustomColumn[] | undefined,
): CustomColumn[] {
  if (!customColumnsRaw?.length) {
    return [];
  }
  return customColumnsRaw.map((column) => {
    const sql = column.sql_expression || '';
    const isAggregate =
      typeof column.is_aggregate === 'boolean'
        ? column.is_aggregate
        : /\b(avg|sum|distinct|max|min|count)\s*\(/i.test(sql);
    return {
      ...column,
      id: column.id || uid(),
      column_custom: true,
      is_aggregate: isAggregate,
    };
  });
}

const initState = () => ({
  gameCode: '',
  system: 'datalab',
  dataTables: [] as IGetTableInfoDto[],
  /**
   * 数据表 schema 是否已确认（含确认后为空）。
   * false：进页/重置后尚未跑完 setTableInfo，禁止 B1 按「不可解析」回退；
   * true：setTableInfo / clearTableInfo 已完成，允许 B1（空表亦可回退）。
   */
  dataTablesSchemaReady: false,
  dataTableJoinRelations: [] as ITableJoinRelation[], // 表之间的关系
  isOpenSelfJoin: false, // 自关联按钮的状态； true为开启状态，fasle为关闭状态
  userPackages: [] as INumberPackage[], // 用户号码包
  customColumns: [] as CustomColumn[], // 自定义字段
	
  columnFilters: [] as IColumnInfo[],
  columnUserFilters: [] as IColumnInfo[],
  columnCompares: [] as IColumnInfo[],
  columnMetrics: [] as IColumnInfo[],
  columnDimensions: [] as IColumnInfo[],
  columnShowDimensions: [] as IColumnInfo[],
  customDimensionMap: {} as DimensionMapInfo,
  columnRawColumns: [] as IColumnInfo[],
  columnX: [] as IColumnInfo[],
  dragState: <DragState>{
    currentDrag: '',
    errorDrag: <ErrorDrag>{},
  },
  condition: {
    group_by: [] as GroupBy[],
    filters: {filters: []} as AnalysisFilters,
    metrics: [] as MetricsItem[],
    orderby: [] as OrderByItem[],
  } as Condition, // 向后台请求计算分析的值
  previousHashForSave: '', // 上次计算的条件hash(用于保存)
  hashEqualForSave: true, // 条件hash是否改变(用于保存)
	
  previousHashForAnalysis: '', // 上次计算的条件hash(用于计算)
  hashEqualForAnalysis: true, // 条件hash是否改变(用于计算)
	
  hadAnalysed: false, // 是否点击过计算按钮
	
  analysisRunning: false,
  analysisReqId: '',
  analysisRefreshRunning: false,
  analysisCacheKey: '',
  table: undefined, // table 数据
  chartData: {}, // 折线图和柱状图数据
  task_id: '',
  chartType: 'Table', // 图表类型
  statisticalMetrics: '', // 统计指标
  saveObj: {
    name: '',
    nameCn: '',
    label: '',
    business_type: 0, // 0: 个人空间 1:业务空间
    type: 0, // 0: 多维分析 1: 探索分析
  },
  autoSelectTable: false, // 是否自动弹出选择数据表弹框
  rowLimit: 5000, // 行限制
  mode: ChartCalculateMode.Aggregate as TChartCalculateMode, // 图表的计算模式: 聚合计算方式, 原始数据的计算方式
  chartDataSourceEditDisabled: false,
  sqlViewUpdateDisabled: false,
  sqlViewEdit: false, // sql视图编辑权限
  hasUserFilters: false,
  visibleSearch: true,
  columnSortToken: {} as Record<string, unknown>,
});

export default {
  namespace,
  state: initState(),
  reducers: {
    ...analysisModel.reducers,
    save(state: any, action: { payload: any; }) {
      const newState = {...state, ...action.payload};
      const {
        gameCode,
        dataTableJoinRelations,
        columnFilters,
        columnUserFilters,
        columnCompares,
        columnMetrics,
        columnDimensions,
        columnShowDimensions,
        customDimensionMap,
        columnRawColumns,
        columnX,
        mode,
        hasUserFilters,
        userPackages,
        customColumns,
        dataTables,
        rowLimit,
        chartType,
      }: {
        gameCode: string;
        dataTableJoinRelations: ITableJoinRelation[];
        columnFilters: IColumnInfo[];
        columnUserFilters: IColumnInfo[];
        columnCompares: IColumnInfo[];
        columnMetrics: IColumnInfo[];
        columnDimensions: IColumnInfo[];
        columnShowDimensions: IColumnInfo[];
        customDimensionMap: DimensionMapInfo;
        columnRawColumns: IColumnInfo[];
        columnX: IColumnInfo[];
        mode: TChartCalculateMode;
        hasUserFilters: boolean;
        userPackages: INumberPackage[];
        customColumns: CustomColumn[];
        dataTables: IGetTableInfoDto[];
        rowLimit: string | number;
        chartType: TChartType;
      } = newState;
			
      // tapd单 【Datalab】公共维度过滤条件优化
      // 分类维度有值时, 过滤条件中公共维度字段的默认值是 全部求和, 否则默认值是全部去重
      // const dimensionIds = newState?.columnDimensions?.map((column: IColumnInfo) => column.id) || [];
      // newState?.columnFilters?.forEach((filter: IColumnInfo) => {
      //   // options_req 如果存在说明用户已经打开过下拉选项进行过操作了, 那么就不需要判断默认值了
      //   if (filter.isCommonDimension && (filter.column_type !== 'date') && (!filter?.options_req)) {
      //     filter.column_value = dimensionIds.includes(filter.id) ? [] : ['255']
      //   }
      //
      // })
      // newState?.columnCompares?.forEach((filter: IColumnInfo) => {
      //   if (filter.isCommonDimension && (filter.column_type !== 'date') && (!filter?.options_req)) {
      //
      //     filter.column_value = dimensionIds.includes(filter.id) ? [] : ['255']
      //   }
      //
      // })
			
      let newHash = '';
      const condition = {
        queries: buildCondition({
          gameCode,
          mode,
          hasUserFilters,
          columnFilters,
          columnUserFilters,
          columnCompares,
          columnMetrics,
          columnDimensions,
          columnX,
          columnShowDimensions,
          columnRawColumns,
          customDimensionMap,
          customColumns,
          chartType,
          rowLimit,
        }),
        table_infos: dataTableJoinRelations,
        data_package_infos: bulidDataPackageInfos(userPackages),
      };
      const conditionStr = JSON.stringify(condition);
      if (conditionStr.length > 0) {
        // newHash = crypto.createHash('sha256').update(conditionStr).digest('base64');
        // newHash = crypto.SHA256(conditionStr).digest('base64');
        newHash = crypto.enc.Base64.stringify(crypto.SHA256(conditionStr));
      }
			
      let newSaveHash = newHash;
      const hashObj = {dataTables};
      const hashStr = JSON.stringify(hashObj);
      if (hashStr.length) {
        // newSaveHash += crypto.createHash('sha256').update(hashStr).digest('base64');
        newSaveHash += crypto.enc.Base64.stringify(crypto.SHA256(hashStr));
      }
			
      // 判断是否出现离开时弹出保存弹框
      const {calculateHashForSave, forceAssignEmptyHashForSave, forceAssignEmptyHashAnalysis, resetState} = action.payload;
      const {previousHashForSave} = resetState ? action.payload : state;
      let hashStateForSave: any;
      let hashStateForAnalysis: any;
      if (forceAssignEmptyHashForSave) {
        // 强制赋空值
        hashStateForSave = {
          // previousHashForSave: crypto.createHash('sha256').update('').digest('base64'),
          previousHashForSave: crypto.enc.Base64.stringify(crypto.SHA256('')),
          hashEqualForSave: false,
        };
      } else if (calculateHashForSave === true) {
        hashStateForSave = {previousHashForSave: newSaveHash, hashEqualForSave: true};
      } else {
        const hashEqualForSave = previousHashForSave.length === 0 || previousHashForSave === newSaveHash;
        hashStateForSave = {hashEqualForSave};
      }
      
      // 判断是否出现重新计算tip
      const {calculateHashForAnalysis} = action.payload;
      const {previousHashForAnalysis} = resetState ? action.payload : state;
      if (calculateHashForAnalysis === true) {
        hashStateForAnalysis = {previousHashForAnalysis: newHash, hashEqualForAnalysis: true};
      } else if (forceAssignEmptyHashAnalysis){
        hashStateForAnalysis = { hashEqualForAnalysis: false};
      } else {
        const hashEqualForAnalysis = previousHashForAnalysis.length === 0 || previousHashForAnalysis === newHash;
        hashStateForAnalysis = {hashEqualForAnalysis};
      }
			
      // 用户在拖拉拽页面 手动点击筛选区域的 字段的删除按钮, 导致筛选区域没有筛选条件了, 相当于清空的效果
      if (mode === ChartCalculateMode.RawColumn && chartType === ChartType.Table) {
        if (columnFilters.length === 0 && columnRawColumns.length === 0) {
          newState.table = {};
          newState.charData = {};
          hashStateForAnalysis.hashEqualForAnalysis = true;
          hashStateForAnalysis.previousHashForAnalysis = newHash;
        }
      } else {
        if (columnFilters.length === 0 && columnCompares.length === 0 && columnMetrics.length === 0 && columnDimensions.length === 0 && columnShowDimensions.length === 0) {
          newState.table = {};
          newState.charData = {};
          hashStateForAnalysis.hashEqualForAnalysis = true;
          hashStateForAnalysis.previousHashForAnalysis = newHash;
        }
        // 针对自定义字段进行清空
        if (isEmpty(dataTables) && !isEmpty(customColumns)) {
          newState.customColumns = [];
          newState.columnFilters = [];
          newState.columnCompares = [];
          newState.columnMetrics = [];
          newState.columnDimensions = [];
          newState.columnRawDimensions = [];
          newState.columnXDimensions = [];
          newState.columnShowDimensions = [];
          newState.customDimensionMap = { options: [], data: [], loading: true};
          newState.Dimensions = [];
          hashStateForAnalysis.hashEqualForAnalysis = true;
          hashStateForAnalysis.previousHashForAnalysis = newHash;
        }
      }
      return {...newState, ...hashStateForSave, ...hashStateForAnalysis};
    },
    saveDrag(state: any, action: { payload: any; }) {
      const {dragState: dragStatePayload} = action.payload;
      if (dragStatePayload) {
        return {...state, dragState: dragStatePayload,};
      }
      const {dragState} = state;
      const {currentDrag, errorDrag} = dragState;
      const value = {currentDrag: '', errorDrag: {...errorDrag, [currentDrag]: ''}};
      return {...state, ...{dragState: value}};
    },
    switchChartCalculateMode(state: any, action: { payload: any; }) {
      const {mode, tables, ...reset} = action.payload;
      const filterData = (arr: any[]) =>
        arr?.filter?.((item) => {
          if (item?.column_custom) return true; // 自定义字段保留, 不删除
          // 说明 操作字段所在的数据表已经被删除, 按照table_code相同的条件下再次查找.
          // 2. 判断 tableCode相同的情况下, 筛选字段 和 表字段是否相同原始名称和类型
          // 这个是为了 用户替换了 和原来数据表字段都相同, 但id不同的数据表 场景
          const tableCodeNumber = Number(item?.table_code?.[1] ?? 0);
          const result = tables[tableCodeNumber]?.column_list?.some(column =>
            ((item?.column || item?.column_name) === column?.name && item?.column_type === column?.type)
          );
          if (result) {
            item.table_id = tables[tableCodeNumber].table_id; // 需要更新原来字段的table_id
            item.id = `${item.table_id}${(item?.column || item?.column_name)}`;
          }
          return result;
        }) || [];
      
      return {
        ...state,
        mode,
        chartType: ChartType.Table,
        columnFilters: tables ? filterData(state.columnFilters) : state.columnFilters,
        // columnCompares: [],
        // columnMetrics: [],
        // columnDimensions: [],
        // columnShowDimensions: [],
        // customDimensionMap: { options: [], data: [], loading: true},
        // columnRawColumns: [],
        ...reset
      };
    },
    updateStringFilter(state: any, action: { payload: any; }) {
      // 当字符串筛选器选项 请求过程中, 有对 过滤条件字段 拖拽删除修改, 等请求结束后 会重置过滤条件到 请求前的状态; 所以在这里增加判断
      const {columnFilters, columnCompares, ...reset} = action.payload;
      let newColumnFilters: IColumnInfo[] = [];
      let newColumnCompares: IColumnInfo[] = [];
      newColumnFilters = state.columnFilters?.map((oldFilter: IColumnInfo) => {
        const newFilter = columnFilters?.find(item => item.id === oldFilter.id);
        return newFilter ?? oldFilter;
      });
      newColumnCompares = state.columnCompares?.map(oldFilter => {
        const newFilter = columnCompares?.find(item => item.id === oldFilter.id);
        return newFilter ?? oldFilter;
      });
      return {
        ...state,
        columnFilters: newColumnFilters || state.columnFilters,
        columnCompares: newColumnCompares || state.columnCompares,
        ...reset
      };

    },

  },
  effects: {
    ...analysisModel.effects,
    * saveCondition(
      {payload, callback}: { payload: any; callback: Function; },
      {put, select}: { put: any; select: any; },
    ): any {
      const {business_type, label, name, nameCn, taskId, game_code, customizeSetting, oldId, save_column_infos} = payload;
      const create_time = moment().format('YYYY-MM-DD HH:mm:ss');
      const {
        gameCode,
        dataTableJoinRelations,
        columnFilters,
        columnCompares,
        columnUserFilters,
        columnMetrics,
        columnDimensions,
        columnShowDimensions,
        customDimensionMap,
        columnRawColumns,
        columnX,
        mode,
        hasUserFilters,
        userPackages,
        customColumns,
        chartType = ChartType.Table,
        statisticalMetrics,
        system,
      }: {
        gameCode: string;
        dataTableJoinRelations: ITableJoinRelation[];
        columnFilters: IColumnInfo[];
        columnCompares: IColumnInfo[];
        columnUserFilters: IColumnInfo[];
        columnMetrics: IColumnInfo[];
        columnDimensions: IColumnInfo[];
        columnShowDimensions: IColumnInfo[];
        customDimensionMap: DimensionMapInfo;
        columnRawColumns: IColumnInfo[];
        columnX: IColumnInfo[];
        mode: TChartCalculateMode;
        hasUserFilters: boolean;
        userPackages: INumberPackage[];
        customColumns: CustomColumn[];
        chartType: TChartType;
        statisticalMetrics: string;
        system: string;
      } = yield select((state: { [x: string]: any; }) => state[namespace]);
      
      const newCondition = buildCondition({
        gameCode,
        mode,
        hasUserFilters,
        columnFilters,
        columnUserFilters,
        columnCompares,
        columnMetrics,
        columnDimensions,
        columnX,
        columnShowDimensions,
        columnRawColumns,
        customDimensionMap,
        customColumns,
        chartType,
        customizeSetting,
      });
      
      const metrics = newCondition.metrics?.[0];
      const metricsName = `${metrics?.table_code}_${metrics?.label}${FuncNameMap[aggregateObj[metrics?.aggregate]] || ''}`;
      // tempData 作为 表格中排序, 分页, 列宽临时存储着用户操作后的状态, 保存图表时再转换成上传数据
      const columnInfos = {
        ...save_column_infos,
        tempData: undefined,
      };
      if (save_column_infos?.tempData) {
        // save_column_infos.tempData 内部存在 不能赋值给columnInfos的属性, 需要采取这种单独赋值的方法
        const {savePaginAtion, saveRowLimits, sorts, saveWidth} = save_column_infos.tempData;
        savePaginAtion && (columnInfos.savePaginAtion = savePaginAtion);
        saveRowLimits && (columnInfos.saveRowLimits = saveRowLimits);
        sorts && (columnInfos.sorts = sorts);
        saveWidth && (columnInfos.saveWidth = saveWidth);
      }
      
      let param: any = {
        filter: JSON.stringify(newCondition),
        table_infos: JSON.stringify(dataTableJoinRelations),
        business_type,
        old_id: oldId,
        label,
        name,
        name_cn: nameCn,
        chart_type: chartType || ChartType.Table,
        statistical_metrics: statisticalMetrics || metricsName,
        create_time,
        game_code,
        data_package_infos: JSON.stringify(bulidDataPackageInfos(userPackages)),
        column_infos: JSON.stringify(columnInfos),
        system,
        // 一期开发只有world地图, 后面再新增地图类型时, 这里要取对应的值
        map_type: chartType === ChartType.Map ? 'world' : undefined,
      };
      if (!checkNullString(taskId)) {
        param = {...param, task_id: taskId};
      }
			
      if (userPackages.length > 0) {
        param = {...param, package_uuid: String(userPackages[0].package_uuid)};
      }
      try {
        const resp = yield service.saveChart(param);
        if (resp.code === 0) {
          yield put({
            type: 'save',
            payload: {
              calculateHashForSave: true,
              save_column_infos: undefined,
              saveObj: {
                name: payload?.name,
                nameCn: payload?.nameCn,
                label: payload?.label,
                business_type: payload?.business_type,
                type: payload?.type ?? 0,
              },
              task_id: resp?.data?.task_id
            },
          });
        }
        callback(param, resp);
      } catch (error) {
        callback(param, error);
      }
    },
		
    * previewSQL(
      {payload, callback}: { payload: any; callback: Function; },
      {select}: { put: any; select: any; },
    ): any {
      try {
        const {
          gameCode,
          condition: oldCondition,
          dataTableJoinRelations,
          columnFilters,
          columnCompares,
          columnUserFilters,
          columnMetrics,
          columnDimensions,
          columnShowDimensions,
          customDimensionMap,
          columnRawColumns,
          columnX,
          mode,
          hasUserFilters,
          userPackages,
          customColumns,
          dataTables,
          rowLimit,
          chartType,
          system,
          save_column_infos: saveColumnInfos,
          customizeSetting,
        }: {
          gameCode: string;
          condition: Condition;
          dataTableJoinRelations: ITableJoinRelation[];
          columnFilters: IColumnInfo[];
          columnUserFilters: IColumnInfo[];
          columnCompares: IColumnInfo[];
          columnMetrics: IColumnInfo[];
          columnDimensions: IColumnInfo[];
          columnShowDimensions: IColumnInfo[];
          customDimensionMap: DimensionMapInfo;
          columnRawColumns: IColumnInfo[];
          columnX: IColumnInfo[];
          mode: TChartCalculateMode;
          hasUserFilters: boolean;
          userPackages: INumberPackage[];
          customColumns: CustomColumn[];
          dataTables: IGetTableInfoDto[];
          rowLimit: string|number;
          chartType: TChartType;
          system: string;
          save_column_infos: any;
          customizeSetting: any;
        } = yield select((state: { [x: string]: any; }) => state[namespace]);

        // isGetTableSheet是true,表示请求的是表格数据
        const {condition, isGetTableSheet, isUseOldCondition} = payload;
				
        let newCondition: Condition;
        if (isUseOldCondition) {
          newCondition = {
            query_mode: mode,
            system,
            ...oldCondition,
            ...condition,
          };
        } else {
          newCondition = buildCondition({
            gameCode,
            mode,
            hasUserFilters,
            columnFilters,
            columnUserFilters,
            columnCompares,
            columnMetrics,
            columnDimensions,
            columnX,
            columnShowDimensions,
            columnRawColumns,
            customDimensionMap,
            customColumns,
            chartType,
            rowLimit,
          }, true);
					
          // 生成新的Condition
          // const newGroupBy: GroupBy[] = createGroupBy(columnDimensions);
          // const newFilters: FiltersItem[] = createFiltersItem(columnFilters);
          // const newFilterCompares: AnalysisFilters[] = createFilterCompares(columnCompares);
          // const newMetrics: MetricsItem[] = createMetricsItem(columnMetrics);
					
          // newCondition = {
          //   group_by: newGroupBy,
          //   filters: {
          //     filters: newFilters.filter((item: FiltersItem) => item.value.length > 0),
          //   },
          //   filter_compares: newFilterCompares.map((analysisFilter: AnalysisFilters) => ({
          //     filters: analysisFilter.filters.filter((filtersItem: FiltersItem) => filtersItem.value.length > 0),
          //   })),
          //   metrics: newMetrics,
          //   orderby: [],
          //   custom_columns: customColumns,
          // };
        }
				
        const reqParams: Analysis = {
          queries: newCondition,
          table_infos: dataTableJoinRelations,
          max_size: +rowLimit,
          pagesize: +rowLimit,
          page: 1,
          is_chart: !isGetTableSheet,
          is_download: false,
          query_sequence_id: '1',
          data_package_infos: (userPackages || []).map((item) => {
            const info: AnalysisPackageInfo = {
              package_uuid: item.package_uuid,
              relate_column: item.relate_column,
            };
            return info;
          }),
          data_tables: dataTables,
          chart_type: chartType.replace('Chart', '').toLowerCase(),
          system,
          // 一期开发只有world地图, 后面再新增地图类型时, 这里要取对应的值
          map_type: chartType === ChartType.Map ? 'world' : undefined,
        };

        // 预览SQL：表格类型直接传递sorts（与计算接口保持一致）
        if (chartType === ChartType.Table) {
          const sorts = saveColumnInfos?.tempData?.sorts || saveColumnInfos?.sorts || customizeSetting?.table?.sorts || [];
          reqParams.sorts = sorts;
        }

        const { finalSql } = yield service.getPreviewSQL(reqParams);
        const replaceSQL = checkNullString(finalSql)
          ? ''
          : finalSql.replace(/\b(SELECT|FROM|WHERE|GROUP BY)\b/gi, '\n$&');
				
        if (callback) {
          callback(replaceSQL);
        }
      } catch (e) {
        console.error(e);
        if (callback) {
          callback(null, e);
        }
      }
    },
    // 保存图表类型
    * saveChartType(
      {payload}: { payload: any; callback: Function; },
      {put}: { put: any; select: any; },
    ): any {
      const {chartType} = payload;
      yield put({
        type: 'save',
        payload: {
          chartType
        },
      });
    },
    * setDataTableJoinRelations(
      {payload}: { payload: any; callback: Function; },
      {put}: { put: any; select: any; },
    ): any {
      const { dataTableJoinRelations, ...rest } = payload || {};
      yield put({
        type: 'save',
        payload: {
          dataTableJoinRelations,
          ...rest,
        },
      });
    },
		
    // 保存选择指标
    * saveMetrics(
      {payload}: { payload: any; callback: Function; },
      {put}: { put: any; select: any; },
    ): any {
      const {statisticalMetrics} = payload;
      yield put({
        type: 'save',
        payload: {
          statisticalMetrics
        },
      });
    },

    // 数字维度趋势卡片 获取展示维度选项
    * getDimensionOption(
      {payload: {currentDrag}, callback}: {payload: any; callback: Function; },
      {put, select}: { put: any; select: any; },
    ): any {
      const {
        gameCode,
        dataTables,
        columnShowDimensions,
        customDimensionMap,
        dataTableJoinRelations,
        system
      }: {
        gameCode: string;
        condition: Condition;
        dataTableJoinRelations: ITableJoinRelation[];
        columnShowDimensions: IColumnInfo[];
        customDimensionMap: DimensionMapInfo;
        columnRawColumns: IColumnInfo[];
        userPackages: INumberPackage[];
        dataTables: IGetTableInfoDto[];
        chartType: TChartType;
        rowLimit: string | number;
        mode: TChartCalculateMode;
        system: string;
      } = yield select((state: { [x: string]: any; }) => state[namespace]); 
      const dragData = currentDrag?.[0] || columnShowDimensions?.[0];
      try {
        const reqParams = {
          dim_name: dragData?.column_name,
          dimension_setting: dragData?.dimension_setting,
          game_code: gameCode,
          has_next: true,
          page_no: 1,
          page_size: 10000,
          table_id: dragData?.table_id,
          system,
        };
        
        if (dragData?.column_custom) {
          reqParams.custom_column = {
            custom_columns: [{
              name_cn: 'CustomField',
              column_type: dragData?.column_type,
              name_en: 'CustomField',
              sql_expression: dragData?.sql_expression,
              accuracy: 2,
            }],
            data_tables: dataTables,
            table_infos: dataTableJoinRelations,
            metrics: [
              {
                table_code: 't0',
                table_id: '',
                column: 'CustomField',
                label: 'CustomField',
                label_en: 'CustomField',
                column_type: dragData?.column_type,
                aggregate: '',
                accuracy: 2,
                to_date_format: '',
              }
            ]
          };
          reqParams.table_id = dataTables?.[0]?.table_id;
          reqParams.expression = dragData?.sql_expression;
        }
      
        const {value_list} = yield getReportDimensionInfo(reqParams);
        const options = value_list?.map((item:any) => ({value: item.value, text: getNullTextOfFilter(item.value)})) || [];

        yield put({
          type: 'save',
          payload: { customDimensionMap: {options, data: customDimensionMap.data, loading: false} },
        });

        if (callback) {
          callback(payloads);
        }

      } catch (e) {
        console.error(e);
        yield put({
          type: 'save',
          payload: { customDimensionMap: {options: [], data: customDimensionMap.data, loading: false} },
        });
        if (callback) {
          callback(null, e);
        }
      }
    },
		
    * getDataAnlysis(
      {payload, callback}: { payload: any; callback: Function; },
      {put, select}: { put: any; select: any; },
    ): any {
      try {
        const requestUniqueId = getRequestUniqueId();
        yield put({
          type: 'save',
          payload: {
            analysisRunning: !payload?.refreshType,
            analysisRefreshRunning: !!payload?.refreshType,
            analysisReqId: requestUniqueId,
          },
        });
        const {
          gameCode,
          condition: oldCondition,
          dataTableJoinRelations,
          columnFilters,
          columnUserFilters,
          columnCompares,
          columnMetrics,
          columnDimensions,
          columnShowDimensions,
          customDimensionMap,
          columnRawColumns,
          columnX,
          userPackages,
          customColumns,
          task_id,
          dataTables,
          chartType: tabChartType = ChartType.Table,
          rowLimit,
          mode,
          hasUserFilters,
          analysisRunning,
          analysisRefreshRunning,
          analysisCacheKey,
          system,
          chartData: modelChartData,
          table: modelTable,
          save_column_infos: saveColumnInfos,
        }: {
          gameCode: string;
          condition: Condition;
          dataTableJoinRelations: ITableJoinRelation[];
          columnFilters: IColumnInfo[];
          columnUserFilters: IColumnInfo[];
          columnCompares: IColumnInfo[];
          columnMetrics: IColumnInfo[];
          columnDimensions: IColumnInfo[];
          columnShowDimensions: IColumnInfo[];
          customDimensionMap: DimensionMapInfo;
          columnRawColumns: IColumnInfo[];
          columnX: IColumnInfo[];
          userPackages: INumberPackage[];
          customColumns: CustomColumn[];
          task_id: string;
          dataTables: IGetTableInfoDto[];
          chartType: TChartType;
          rowLimit: string | number;
          mode: TChartCalculateMode;
          hasUserFilters: boolean;
          analysisRunning: boolean;
          analysisRefreshRunning: boolean;
          analysisCacheKey: string;
          chartData: any;
          table: any;
          system: string;
          save_column_infos: any;
        } = yield select((state: { [x: string]: any; }) => state[namespace]);     
        // isGetTableSheet是true,表示请求的是表格数据
        const {
          condition,
          isGetTableSheet,
          isUseOldCondition,
          customizeSetting,
          gameCode: game_code,
          isSetWarnInfo = false,
          refreshType,
          newColumnFilters,
          isNoSave,
          updateChartSettingForm,
          cache_key: payloadCacheKey,
          reuseCacheKey = false,
        } = payload;

        // 请求计算的图表类型(可能来自切换图表类型组件refreshType, 也可能来自请求计算的图表类型tabChartType)
        const chartType: TChartType = refreshType || tabChartType;
        let newCondition: Condition;
        if (isUseOldCondition) {
          newCondition = {
            query_mode: mode,
            system,
            ...oldCondition,
            ...condition,
          };
        } else {
          // 生成新的Condition
          newCondition = buildCondition({
            gameCode: gameCode || game_code,
            mode,
            hasUserFilters,
            columnFilters: newColumnFilters || columnFilters,
            columnUserFilters,
            columnCompares,
            columnMetrics,
            columnDimensions,
            columnX,
            columnShowDimensions,
            columnRawColumns,
            customDimensionMap,
            customColumns,
            chartType,
            rowLimit,
          }, true);
					
					
          // const newGroupBy: GroupBy[] = createGroupBy(columnDimensions);
          // const newFilters: FiltersItem[] = createFiltersItem(columnFilters);
          // const newFilterCompares: AnalysisFilters[] = createFilterCompares(columnCompares);
          // const newMetrics: MetricsItem[] = createMetricsItem(columnMetrics);
					
          // newCondition = {
          //   group_by: newGroupBy,
          //   filters: {
          //     filters: newFilters.filter((item: FiltersItem) => item.value.length > 0),
          //   },
          //   filter_compares: newFilterCompares.map((analysisFilter: AnalysisFilters) => ({
          //     filters: analysisFilter.filters.filter((filtersItem: FiltersItem) => filtersItem.value.length > 0),
          //   })),
          //   metrics: newMetrics,
          //   orderby: [],
          //   custom_columns: customColumns,
          // };
        }

        const requestCacheKey =
          payloadCacheKey ||
          (reuseCacheKey
            ? analysisCacheKey || createAnalysisCacheKey(task_id)
            : createAnalysisCacheKey(task_id));

        if (requestCacheKey !== analysisCacheKey) {
          yield put({
            type: 'save',
            payload: {
              analysisCacheKey: requestCacheKey,
            },
          });
        }

        const reqParams: Analysis = {
          queries: { ...newCondition, customizeSetting },
          task_id,
          table_infos: dataTableJoinRelations,
          pagesize: +rowLimit,
          max_size: +rowLimit,
          page: 1,
          is_chart: !isGetTableSheet,
          is_download: false,
          query_sequence_id: requestUniqueId,
          data_package_infos: bulidDataPackageInfos(userPackages),
          data_tables: dataTables,
          cache_key: requestCacheKey,
          chart_type: chartType?.replace('Chart', '')?.toLowerCase(),
          // 一期开发只有world地图, 后面再新增地图类型时, 这里要取对应的值
          map_type: chartType === ChartType.Map ? 'world' : undefined,
          system,
        };
        // 表格-数据交叉透视表：增加继承交叉前的维度排序
        if (chartType === ChartType.Table) {
          const crosstab = customizeSetting?.table?.crosstab || {};
          // 排除「对照组」字段后再判断交叉表，因为对照组不是真正的维度
          const crosstabRowsWithoutCompare = crosstab?.rows?.filter(
            (row: { column?: string; }) => row?.column !== DATALAB_COMPARE_USER_FILTERS
          );
          // 交叉表行数（不含对照组）小于维度数, 表示有交叉表
          const isCrosstab = crosstabRowsWithoutCompare?.length < columnDimensions?.length;
          if (isCrosstab) {
            const sorts = saveColumnInfos?.tempData?.sorts || saveColumnInfos?.sorts || customizeSetting?.table?.sorts || [];
            // 交叉前后，保持维度值的排序效果
            const dimensionsSorts = sorts?.filter((item: {code: string; order: "asc" | "desc";}) =>
              columnDimensions?.some((dimension: IColumnInfo) => `${dimension?.table_code || 't0'}_${dimension?.column_display}` === item.code)
            );
            reqParams.sorts = dimensionsSorts;
          }
        }
        // if (new_flag) reqParams.new_flag = true; 后端说已经不需要这个flag了
        const {
          actual_count: actualCount,
          table_data: tableData, // 表格数据(一位数组) // 计算数据为空返回了null
          // [legacy · 重构可删] 后端已不再返回 extra_table_data（实测恒为 undefined）。
          // 统计行现由 table_data 行内 is_statistics / statistics_name 承载 → AliTableCell → StatisticsBubble；
          // 排序不参与重排见 AliTableAnalytics/utils compareStatisticsLast。
          extra_table_data: statisticsData,
          bar_percentage_min_max: barPercentageMinMax, // 柱状图条件格式数据
          chart_data: chartData, // 除折线图和柱状图以及事件折线数据(二维数组) // 计算数据为空返回了null
          event_list: eventData, // 事件折线图事件详情数据, 为空返回了null
          event_line_chart: eventLineChart, // 事件折线图计算数据, 为空返回了null
          line_chart: lineChart, // 折线图计算数据, 为空返回了null
          chart_sum_data: pieChartData, // 饼图数据(一维数组) // 计算数据为空返回了null
          metrics, // 指标(一维数组)(图表用)
          metrics_en, // 指标(一维数组)(图表用)
          column_name: columnName, // 表格头名字(一维数组)
          crosstab_columns: crosstabColumns, // 交叉表透视表列数据
          count, // 表格总列数
          trend_metric, // 计算数据为空, 正常返回对象结构, 属性值都是空字符串
          big_number_charts: bigNumberChart, // 数字卡片新接口返回的参数.
          // 柱图
          bar_charts: barChart,
          // barCharts: barChartData, // 计算数据为空返回了空数组 []
          // 事件折线
          // eventLineChartSumData, // 计算数据为空返回了null
          // 折线数据
          // lineChartSumData, // 计算数据为空返回了null
          // 是否历史报表
          // new_flag: newFlag, // 也不需要接收这个flag了
          // 组合图数据
          mixed_chart: mixedChartData, // 计算数据为空返回了null
          // 堆叠图数据
          // stack_charts: stackCharts, // 计算数据为空返回了null
          stack_bar_chart: stackBarChart,
          // [legacy · 重构可删] 后端已不返回 axis_list；x 轴在 percent_stack_bar_chart.x_axis_data
          // axis_list: axisList,
          // 数字趋势维度卡片的折线图
          trend: dimChartData, // 计算数据为空返回了null
          // 数字趋势维度卡片的趋势图
          dimension: rankData, // 计算数据为空返回了null
          // 地图数据
          // 计算出来没有数据, 不返回null
          // mapCharts: [{
          //                     "metric": "t0_steam_review_negative ",
          //                     "value_type": "指标对应的类型",
          //                     "min": "0",
          //                     "max": "0",
          //                     "map_chart_value": []
          // }]
          map_charts: mapDatas,
          word_cloud: wordCloudData,
          horizontal_bar_charts: horizontalBarData,
          percent_stack_bar_chart: percentStackBarData,
          percent_line_chart: percentLineChartWire,
        } = yield service.calculateAnalysisChart(chartType, reqParams).catch((err) => {
          if (callback) {
            // 如果是单个图表接口错误，不能影响其他图表
            const errorData = {
              condition: newCondition,
              forceAssignEmptyHashForSave: !task_id,
              calculateHashForAnalysis: true,
              analysisRunning: false,
              analysisRefreshRunning: false,
              analysisReqId: '',
              dragState: {
                currentDrag: '',
                errorDrag: {},
              },
              errorMsg: err.msg || err.data,
              errorData: err.data,
              errorCode: err.code,
            };
            if (err.code === 10013) {
              // 10013是图表计算时, 表字段缺失错误码
              // 因为需要后端返回 字段名称 和 报错信息, 给到前端进行拼接, 但是后端又不能赋值给data对象类型, 以及修改msg
              // 所以只能采取拼接分割的方式
              const info = err?.data?.split('$$$$$') || [];
              errorData.errorMsg = info[1];
              errorData.errorData = info[0];
            }
            callback({
              table: undefined,
              chartData: {},
              ...errorData
            });
          }
        }) || {};
        if (analysisRunning || analysisRefreshRunning) {
          // 处理表格数据
          let newTableData = tableData || [];
          if (chartType === ChartType.Table) {
            newTableData = (tableData || [])?.map((item: ITableAnalyticsData) =>
              Object.keys(item).reduce((acc: ITableAnalyticsData, key) => {
                const value = item?.[key]?.value;
                // 数据兜底兼容
                acc[key] = {
                  ...item[key],
                  value: convertToString(value)
                };
                return acc;
              }, {}));
          }
          const payloads: any = {
            table: {
              crosstabColumns,
              columnName,
              count,
              data: newTableData,
              actualCount,
              barPercentageMinMax,
              // [legacy · 重构可删] 依赖 extra_table_data → formatStatisticsData → appendUnsortedData；
              // 后端不返回 extra_table_data 时恒为 []，统计展示不经过此字段。
              statisticsData: formatStatisticsData(statisticsData) || [],
            },
            chartData: {
              metrics,
              metrics_en,
              columnName,
              // trend_metric: trend_metric ?? {},
              // bigNumberChart: bigNumberChart ?? {}
            },
            // newFlag: getNewFlagNumber(chartType, newFlag)
          };
          // 如果refreshType有值, 图表切换类型，将不同类型数据缓存到chartData中
          if (refreshType) {
            payloads.table = {
              ...(modelTable || {}),
              ...(refreshType === ChartType.Table ? payloads?.table || {} : {}),
            };
            payloads.chartData = {
              ...(modelChartData || {}),
              ...(refreshType === ChartType.Bar ? {
                barChart: barChart && {
                  xAxisData: barChart.x_axis_data,
                  seriesData: barChart.series_data,
                },
              } : {}),
              ...(refreshType === ChartType.Line ? {
                lineChart: lineChart && {
                  xAxisData: lineChart.x_axis_data,
                  seriesData: lineChart.series_data,
                },
              } : {}),
            };
          }
          // 如果是顶部图表tabChartType切换，使用旧逻辑单一数据源
          switch (refreshType ? false : tabChartType) {
            case ChartType.StackBar: {
              // payloads.chartData.stackCharts =
              payloads.chartData.stackBarChart = stackBarChart && {
                xAxisData: stackBarChart.x_axis_data,
                seriesData: stackBarChart.series_data,
              };
              // payloads.chartData.axisList = axisList ?? [];
              break;
            }
            case ChartType.Bar: {
              payloads.chartData.barChart = barChart && {
                xAxisData: barChart.x_axis_data,
                seriesData: barChart.series_data,
              };
              // payloads.chartData.axisList = barChart.x_axis_data ?? [];
              break;
            }
            case ChartType.Line: {
              payloads.chartData.lineChart = lineChart && {
                xAxisData: lineChart.x_axis_data,
                seriesData: lineChart.series_data,
              };
              // payloads.chartData.axisList = lineChart.x_axis_data ?? [];
              break;
            }
            case ChartType.MixedChart: {
              if (
                mixedChartData
                && hasEmptyProperty(mixedChartData, ['series_data', 'x_axis_data'])
              ) {
                payloads.chartData.mixedChartData = {};
              } else  {
                payloads.chartData.mixedChartData = mixedChartData ?? {};
              }
              break;
            }
            case ChartType.EventLine: {
              // payloads.chartData.eventLineChartSumData = eventLineChartSumData ? (eventLineChartSumData[0] ? eventLineChartSumData : []) : [];
              
              payloads.chartData.eventLineChart = eventLineChart && {
                xAxisData: eventLineChart.x_axis_data,
                seriesData: eventLineChart.series_data,
              };
              payloads.chartData.eventData = eventData ?? [];
              // payloads.chartData.axisList = axisList ?? [];
              break;
            }
            case ChartType.Pie: {
              payloads.chartData.pieChartData = pieChartData ? (pieChartData?.[0] ? pieChartData : []) : [];
              break;
            }
            case ChartType.BigNumber: {
              payloads.chartData.bigNumberChart = bigNumberChart ?? [];
              break;
            }
            case ChartType.Trend: {
              payloads.chartData.trend_metric = trend_metric ?? {};
              payloads.chartData.lineData = chartData ?? [];
              break;
            }
            case ChartType.DimNumberTrend: {
              payloads.chartData.lineData = hasEmptyProperty(dimChartData?.[0], ['series', 'xAxis']) ? [] : dimChartData;
              payloads.chartData.rankData = hasEmptyProperty(rankData?.[0], ['series']) ? [] : rankData;
              payloads.chartData.trend_metric = trend_metric ?? {};
              break;
            }
            case ChartType.Map: {
              // TODO 涉及到 展示空态或者操作指引的额问题
              payloads.chartData.mapData = mapDatas?.[0];
              break;
            }
            case ChartType.WordCloud: {
              // 兼容后端返回null的情况
              payloads.chartData.wordCloudData = wordCloudData || {};
              break;
            }
            case ChartType.HorizontalBarChart: {
              payloads.chartData.horizontalBarData = horizontalBarData || [];
              break;
            }
            case ChartType.PercentStackBar: {
              payloads.chartData.percentStackBarData = {
                xAxisData: percentStackBarData?.x_axis_data || [],
                seriesData: percentStackBarData?.series_data || [],
              };
              // [legacy · 重构可删] axis_list 已废弃，PercentStackBar 仅用 percentStackBarData.xAxisData
              // payloads.chartData.axisList = axisList ?? [];
              break;
            }
            case ChartType.PercentLine: {
              payloads.chartData.percentLineChartData = mapPercentAxisWireToChartData(
                percentLineChartWire,
              );
              break;
            }
          }
          
          const savePayload = {
            ...payloads,
            reqParams,
            condition: {
              ...newCondition,
            },
            forceAssignEmptyHashForSave: !task_id,
            calculateHashForAnalysis: !isSetWarnInfo,
            hadAnalysed: true,
            analysisRunning: false,
          };
          isNoSave && (delete savePayload.condition);
          yield put({
            type: 'save',
            payload: savePayload,
          });
          
          if (callback) {
            callback(payloads);
          }
        }
      } catch (e) {
        console.error(e);
        yield put({
          type: 'save',
          payload: {
            hadAnalysed: true,
            analysisRunning: false,
            analysisRefreshRunning: false,
          },
        });
      }
    },
    
    * cancelAnalysis({payload}: { payload: any; }, {put, select}: { put: any; select: any; }): any {
      const {analysisRunning, analysisReqId} = yield select((state: { [x: string]: any; }) => state[namespace]);
      if (analysisRunning) {
        yield put({
          type: 'save',
          payload: {
            analysisRunning: false,
            analysisReqId: '',
          },
        });
        if (!checkNullString(analysisReqId)) {
          try {
            yield service.cancelAnalysis(analysisReqId);
            payload?.cancelTip?.('success');
          } catch (error) {
            payload?.cancelTip?.('success');
            console.log(error);
          }
        }
      }
    },
    //
    * getTableInfo(
      {payload, callback}: { payload: any; callback: Function; },
      {put, select}: { put: any; select: any; },
    ): any {
      const {
        taskId,
        packageUuids,
        tableIds,
        gameCode,
        autoSelectTable,
        system,
      }: { tableIds: string[]; packageUuids: number[]; taskId: number; gameCode: string; autoSelectTable: boolean; system: string; } = payload;
      let {
        dataTableJoinRelations = [],
        // eslint-disable-next-line prefer-const
        userPackages = [],
        save_column_infos,
      } = yield select((state: { [x: string]: any; }) => state[namespace]);
      let tableRsp: any;
      let taskRsp: any;
      let taskPackageUUid: number[] = packageUuids;
      let packageInfo = [];  
      if (taskId) {
        // 获取图表详情信息
        taskRsp = yield service.getChartDetail({
          task_id: taskId,
          game_code: gameCode,
          system,
        });
        dataTableJoinRelations = JSON.parse(taskRsp.table_infos);
        packageInfo = JSON.parse(taskRsp?.data_package_infos || "[]") || userPackages;
        taskPackageUUid =
					!checkNullString(taskRsp.package_uuid)
					  ? [taskRsp.package_uuid]
					  : taskPackageUUid;
        const tableIdList = dataTableJoinRelations ? dataTableJoinRelations.map((item: any) => item.table_id) : [];
				
        // 从已保存的图表进入拖拉拽分析页面时, 获取数据表信息
        tableRsp = yield service.getTableInfoList(tableIdList, gameCode, system);
        
        const { sorts, ...rest } = JSON.parse(taskRsp.column_infos || '{}');
        save_column_infos = {
          // 旧图表保存时, 后端返回的字段名称有空格
          // 现在后端返回的字段名称缺少了之前数据的空格.
          // 导致 进行比对的时候, 无法相等, 排序失效
          // 所以这里将保存好的sorts中的字段名称去掉空格
          sorts: sorts?.map((item: any) => ({ ...item, code: item?.code?.trim() })),
          ...rest,
        };
              
				
      } else if (!taskId && tableIds.length > 0) {
        // 从数据表管理页面进入拖拉拽分析页面时, 获取数据表信息
        tableRsp = yield service.getTableInfoList(tableIds, gameCode, system);
        const findTable = tableRsp.find((item: any) => item.table_id === tableIds[0]);
        dataTableJoinRelations =
					tableIds.length === 1
					  ? [
					    {
					      customize_name: findTable?.customize_name,
					      customize_name_en: findTable?.customize_name_en,
					      table_name: findTable?.table_name,
					      table_id: tableIds[0],
					      table_code: 't0',
					      only_one_table: true,
					      granularity:
                  findTable?.cycle ?? DateGranularity.DAY,
					    },
					  ]
					  : undefined;
        save_column_infos = undefined;
				
        // 不自动弹出一定是一张表, 且存在模板id的情况下, 自动请求模板详情
        if (!autoSelectTable && tableRsp[0]?.template_list?.[0]) {
          taskRsp = yield service.getChartDetail({
            task_id: tableRsp[0]?.template_list?.[0].task_id,
            game_code: gameCode,
            system,
          });
        }
      }
			
      if (!tableRsp) {
        if (callback) {
          callback(undefined, taskPackageUUid);
        }
        return;
      }

      // 须在 setTableInfo 前解析：B1 依赖 customColumns 解析字段排序引用
      const filterInfo = JSON.parse(taskRsp?.filter || '{}');
      const earlyCustomColumns = taskId
        ? normalizeCustomColumnsFromFilter(filterInfo?.custom_columns)
        : undefined;
      
      yield put({
        type: 'save',
        payload: {
          dataTableJoinRelations: dataTableJoinRelations || [],
          isOpenSelfJoin: dataTableJoinRelations?.length === 2 && (dataTableJoinRelations[0]?.table_id === dataTableJoinRelations[1]?.table_id),
          userPackages: packageInfo,
          save_column_infos,
          trendStatus: save_column_infos?.trendStatus || {},
          chartDataSourceEditDisabled: taskRsp?.chart_datasource_edit === 'disable',
          sqlViewUpdateDisabled: taskRsp?.virtual_table_edit === 'disable',
          ...(earlyCustomColumns !== undefined
            ? { customColumns: earlyCustomColumns }
            : {}),
          ...buildTaskRestoreFields(taskRsp, taskId),
        },
      });
			
      yield put({
        type: 'setTableInfo',
        payload: {
          response: tableRsp,
          filters: [].concat(filterInfo?.filter_compares?.[0]?.filters, filterInfo?.filters?.filters, filterInfo?.group_by, filterInfo?.metrics, filterInfo?.raw_columns),
          save_column_infos,
          // 显式传入，避免仅依赖 select 时与 save 时序竞态
          ...(earlyCustomColumns !== undefined
            ? { customColumns: earlyCustomColumns }
            : {}),
        },
      });
      if (callback) {
        callback(taskRsp, taskPackageUUid);
      }
    },
		
    * setTableInfo(
      {payload, callback}: { payload: any; callback: Function; },
      {put, select}: { put: any; select: any; },
    ): any {
      const {response, filters, save_column_infos, sql, customColumns: payloadCustomColumns} = payload;
      const dataTables: IGetTableInfoDto[] = [];
      // 设置数据库类型前后端转换, 后端建议除了postgres都外, 前端都使用mysql类型
      const driverMap: any = {
        mysql: 'mysql',
        starrocks: 'mysql',
        postgres: 'postgresql',
        bigquery: 'bigquery',
        clickhouse: 'mysql',
        sqlserver: 'mysql'
      };

      const {
        dataTableJoinRelations,
        isOpenSelfJoin,
        mode
      }: { dataTableJoinRelations: ITableJoinRelation[]; isOpenSelfJoin: boolean; save_column_infos: Object; mode: string; } = yield select(
        (state: { [x: string]: any; }) => state[namespace],
      );
      const onlyOneTable: boolean = response?.length === 1;
      (response || []).forEach((tableInfoRes: any, index: number) => {
        const tableCode = dataTableJoinRelations.find((item) => item.table_id === tableInfoRes.table_id)?.table_code;
        const tableInfo: IGetTableInfoDto = {
          only_one_table: onlyOneTable,
          table_id: tableInfoRes.table_id,
          // 增加 自关联情况下的table_code赋值情况
          table_code: (tableCode && (!isOpenSelfJoin)) ? tableCode.toLowerCase() : `t${index}`,
          table_name: tableInfoRes?.table_name,
          customize_name: tableInfoRes?.customize_name,
          customize_name_en: tableInfoRes?.customize_name_en,
          table_desc: tableInfoRes?.table_desc,
          cycle: tableInfoRes?.cycle,
          creator: tableInfoRes?.creator,
          label: tableInfoRes?.label,
          sub_label: tableInfoRes?.sub_label,
          hide: false,
          column_list: tableInfoRes?.column_list,
          column_class_list: [],
          driver: driverMap[tableInfoRes?.driver] || 'mysql',
          database_uuid: tableInfoRes.database_uuid,
          kind: tableInfoRes?.kind,
          template_list: tableInfoRes?.template_list,
          db_name: tableInfoRes?.db_name,
          sql,
        };
        const tableColunmClass: ITableColumnClassDto[] = [
          // {
          //   name: '公共',
          //   name_en: 'Public',
          //   columns: [],
          // },
          {
            name: '维度',
            name_en: 'Dimension',
            columns: [],
          },
          {
            name: '指标',
            name_en: 'Metric',
            columns: [],
          },
        ];
        (tableInfoRes?.column_list || []).forEach((colunm: TEnrichedTableColumn) => {
          // if (colunm.is_common_dimension) {
          //   if (numberType.values.includes(colunm.type)) {
          //     colunm.type = 'string';
          //   }
          //   colunm.isCommonDimension=true;
          //
          //   tableColunmClass[0].columns.push(convertTableColumnToColumnRawInfo(colunm, tableInfo, index));
          // }
					
          /**
          字段数据恢复要兼容以下情况
					 * 图表的状态:
           1. 过去已保存的旧图表分析任务
           2. 半旧半新的图表分析任务 (在旧的图表中, 有更改)
           3. 完全是新的图表分析任务.
					 * 要求:
           在数据表管理中更改了字段, 不能影响旧的图表分析任务中的字段, 旧图表分析任务字段要维持原来的值
           在图表分析任务中更改了字段, 只影响更改的字段, 原来的字段保持不变.
					 * 方案:
           1. 此次改造在图表保存接口新增了column_infos字段, 上传保存用户更改过的字段信息; 当恢复图表分析任务时, 后端回传column_infos, 前端将字段信息恢复成用户保存的值.
           2. 由于旧的图表分析任务没有column_infos, 一旦旧图表分析任务中的表字段被更改后, 在恢复图表分析任务的时候会显示新的字段值. 为了实现字段的更改不影响旧表的要求, 还需要接口中的filter恢复旧图表的字段值, 而不会使用新的值 (由于过滤条件返回的信息中没有字段的中英显示文本, 只有原始值column, 所以无法做到恢复字段旧的显示文本)
           3. column_infos和filter的区别:
              column_infos是记录全部更改字段的数据. 比如: 左侧列表中的字段更改了, 但是这个字段没有拖拽到右侧的筛选区域, 那么这个字段只有在column_infos中保存了, filter中没有保存. 但是在恢复图表分析任务时, 只使用filter无法恢复左侧列表中被更改的字段数据
              由于旧的图表分析任务中只有filter, 以及半旧半新的图表分析任务中同时存在column_infos和filter, 所以需要它们两个配合恢复字段数据.
           (旧图表分析任务中保存的filter字段数据中只有 label, label_en, column_type 是和现在的字段编辑更新的属性有关, 只需要处理这三个字段就可以了, 在恢复旧图表分析任务时, 其他字段编辑的属性使用默认值.)
					 */
          const filter = filters?.find((item: any) => item?.column === colunm?.name);
          const updateColumn: Object = save_column_infos?.[tableInfoRes.table_id]?.[colunm.name];
          if (filter && updateColumn) { // 说明更改的字段被拖拽到右侧筛选操作区域了 (filter可能是旧的图表分析任务保存的数据)
            const newColumn = {
              ...updateColumn,
              type: updateColumn.column_type || filter.column_type || colunm.type,
              to_date_format: updateColumn.to_date_format || filter.to_date_format || colunm.to_date_format,
              display_name: updateColumn.display_name || filter.label || colunm.display_name,
              display_name_en: updateColumn.display_name_en || filter.label_en || colunm.display_name_en,
            };
            Object.assign(colunm, newColumn);
          } else if (updateColumn) { // 说明更改的字段没有被拖拽到右侧筛选的操作区域
            Object.assign(colunm, updateColumn);
            colunm.type = updateColumn.column_type || colunm.type;
          } else if (filter) { // 说明是旧的图表分析任务中的字段
            const newColumn = {
              type: filter.column_type || colunm.type,
              to_date_format: filter.to_date_format || colunm.to_date_format,
              display_name: filter.label || colunm.display_name,
              display_name_en: filter.label_en || colunm.display_name_en,
              is_dimension: (filter.attribute || colunm.attribute) === 1,
              is_metric: (filter.attribute || colunm.attribute) === 2,
              flag: filter.flag === undefined ? colunm.flag : filter.flag,
              dimension_setting: filter.dimension_setting || colunm.dimension_setting,
              attribute: filter.attribute || colunm.attribute,
              accuracy: filter.accuracy || colunm.accuracy,
            };
            Object.assign(colunm, newColumn);
          } else {
            // 如果 column 字段不存在 操作区的字段里了, 那么在 表格类型的图表配置中删除对应的字段
            const tableSetting = save_column_infos?.chartsCfgSave?.Table?.customizeSetting?.table;
            const description = tableSetting?.description;
            const field_setting = tableSetting?.field_setting;
            if (description) {
              delete description[`${tableInfoRes.table_code}_${colunm?.name}`];
            }
            if (field_setting) {
              delete field_setting[`${tableInfoRes.table_code}_${colunm?.name}`];
            }
          }

          if (colunm.is_dimension || colunm.is_common_dimension) {
            colunm.isCommonDimension = (!colunm.is_metric) && (colunm.dimension_setting === 'system') && (colunm.flag == 0);
            // is_common_dimension是后端返回的, isCommonDimension是前端根据新的规则进行计算的,俩结果可能不一致, 导致后期根据isCommonDimension的判断结果有问题
            // 但 is_dimension一定是true
            colunm.is_dimension = true;
						
            tableColunmClass[0].columns.push(convertTableColumnToColumnRawInfo(colunm, tableInfo, index));
          } else if (colunm.is_metric) {
            tableColunmClass[1].columns.push(convertTableColumnToColumnRawInfo(colunm, tableInfo, index));
          }
        });
        // 注释代码, 避免 从维度转换成指标后，字段直接消失了
        // tableColunmClass = tableColunmClass.filter((item) => item.columns.length > 0);
        tableInfo.column_class_list = tableColunmClass;
				
        dataTables.push(tableInfo);
      });
      dataTables.sort((a: IGetTableInfoDto, b: IGetTableInfoDto) =>
        (a.table_code ? a.table_code : '') < (b.table_code ? b.table_code : '') ? -1 : 1,
      );

      const {
        customColumns: stateCustomColumns,
        chartType,
      }: {
        customColumns: CustomColumn[];
        chartType: string;
      } = yield select((state: { [x: string]: any; }) => state[namespace]);
      // 进页优先用 payload（getTableInfo 已从 filter 早灌）；否则用 state
      const customColumns = (payloadCustomColumns ?? stateCustomColumns) || [];

      // B1：schema 已确认；引用在新表结构中解析不到 → base+draft+save 回默认
      applyDatasetFieldSortResetIfUnresolved(
        dataTables,
        customColumns,
        true,
      );
      const nextSaveColumnInfos = pruneSaveColumnInfosOnDatasetFieldSortUnresolved(
        save_column_infos,
        chartType as TChartType,
        dataTables,
        customColumns,
        true,
      );

      yield put({
        type: 'save',
        payload: {
          dataTables,
          dataTablesSchemaReady: true,
          save_column_infos: nextSaveColumnInfos,
          sqlViewEdit: response?.[0]?.edit,
        },
      });
      if (callback) {
        callback(dataTables, mode);
      }
    },
		
    * getTaskDetail(
      {payload, callback}: { payload: any; callback: Function; },
      {put, select}: { put: any; select: any; },
    ): any {
      try {
        const {task_id, response, inheritFilters, inheritUserFilter} = payload;
        const {
          dataTables,
          columnFilters,
          columnCompares,
          columnMetrics,
          columnDimensions,
          columnX,
          columnShowDimensions,
          columnRawColumns,
          save_column_infos,
        } = yield select(
          (state: { [x: string]: any; }) => state[namespace],
        );
        const chartType = response.chart_type;
        const condition = JSON.parse(response.filter);
        const {
          filters,
          filter_compares,
          dimension,
          group_by,
          metrics,
          custom_columns,
          query_mode = ChartCalculateMode.Aggregate,
          user_filter,
        } = condition;
				
        const newColumnFilters: IColumnInfo[] = [];
        const newColumnFilterCompares: IColumnInfo[] = [];
        const newColumnMetrics: IColumnInfo[] = [];
        const newColumnDimensions: IColumnInfo[] = [];
        const newColumnX: IColumnInfo[] = [];
        const newColumnShowDimensions: IColumnInfo[] = [];
        const newCustomDimensionMap: DimensionMapInfo = { options: [], data: [], loading: true};
        const newColumnRawColumns: IColumnInfo[] = [];
        const newInheritFilters: IColumnInfo[] = [];
				
        function findResultAndLocation<T>(source: T[], find: { item: IColumnRawInfo; tableId: string; tableCode: string | undefined; }): [T | undefined, number] {
          const {item, tableId, tableCode} = find;
          let location = -1;
          const result: T | undefined = source?.find(
            (filterItem, index: number) => {
              const temp = filterItem.column === item.column_name && filterItem.table_id === tableId && filterItem.table_code === tableCode;
              temp && (location = index);
              return temp;
            }
          );
          return [result, location];
        }
				
        dataTables.forEach((tableInfo: IGetTableInfoDto) => {
          const tableId = tableInfo.table_id;
          const tableCode = tableInfo.table_code;
          tableInfo.column_class_list?.forEach((tableItem: ITableColumnClassDto) => {
            tableItem.columns?.forEach((item: IColumnRawInfo) => {
              const findObj = {tableId, tableCode, item};
              const [filtersResult, filterIndex] = findResultAndLocation(filters?.filters, findObj);
              const [inheritFilter] = findResultAndLocation(inheritFilters, findObj);
              
              const isSameType = item?.column_type === inheritFilter?.column_type;
              
              if (filtersResult) {
                let newFilter: IColumnInfo = {
                  ...item,
                  operation: filtersResult.operation,
                  column_value: filtersResult.value,
                  lastCount: filtersResult.lastCount ?? (filtersResult.operation === 'recent' && item.column_granularity === 'day' ? 7 : 1),  // 如果字段原本是周或者月, 那么默认是1周, 1月
                  dateType: filtersResult.dateType || (filtersResult.operation === 'recent' && item.column_granularity || 'day'),
                  from: filtersResult?.last_day,
                  compareTimes: filtersResult?.compare_time,
                  quickValue: filtersResult?.quick_value,
                  filters: filtersResult?.filters, // String类型字段下拉选的过滤条件
                };

                newFilter = {
                  ...newFilter,
                  operation: DBColumnType.getFilterOperation(newFilter),
                };
  
                // 字符串筛选器自定义选项
                !isEmpty(filtersResult.customValues) && (newFilter.customValues = filtersResult.customValues);
                
                if (filtersResult.is_full !== undefined) {
                  newFilter.isAll = filtersResult.is_full;
                } else {
                  // 兼容旧数据
                  const column_value = DBColumnType.getFilterValues(newFilter);
                  newFilter.isAll = column_value.length < 1;
                }
                
                // 先继承探索页面传递的筛选器
                Object.assign(newFilter, isSameType ? inheritFilter : {});
                // 动态日期时间, 计算接口上传时, 需要根据column_text的内容传递value
                newFilter.column_text = DBColumnType.getFilterText(newFilter, true);
                newColumnFilters[filterIndex] = newFilter;
                
              } else if (inheritFilter) {
                isSameType && newInheritFilters.push({...item, ...inheritFilter});
              }
              
              // 旧的对比项交互已删除
              // if (filter_compares?.[0]) {
              //   const [filterCompareResult, compareIndex] = findResultAndLocation(filter_compares[0].filters, findObj);
              //   if (filterCompareResult) {
              //     let newFilter: IColumnInfo = {
              //       ...item,
              //       operation: filterCompareResult.operation,
              //       column_value: filterCompareResult.value,
              //       lastCount: filterCompareResult.lastCount ?? (filterCompareResult.operation == 'recent' && item.column_granularity === 'day' ? 7 : 1),  // 如果字段原本是周或者月, 那么默认是1周, 1月
              //       dateType: filterCompareResult.dateType || (filterCompareResult.operation == 'recent' && item.column_granularity || 'day'),
              //       from: filterCompareResult?.last_day,
              //     };
              //     newFilter = {
              //       ...newFilter,
              //       operation: DBColumnType.getFilterOperation(newFilter),
              //     };
              //     // 字符串筛选器自定义选项
              //     !isEmpty(filterCompareResult.customValues) && (newFilter.customValues = filterCompareResult.customValues);
              //
              //     if (filterCompareResult?.is_full !== undefined) {
              //       newFilter.isAll = filterCompareResult?.is_full;
              //     } else {
              //       // 兼容旧数据
              //       const column_value = DBColumnType.getFilterValues(newFilter);
              //       newFilter.isAll = column_value.length < 1;
              //     }
              //     // 动态日期时间, 计算接口上传时, 需要根据column_text的内容传递value
              //     newFilter.column_text = DBColumnType.getFilterText(newFilter, true);
              //     newColumnFilterCompares[compareIndex] = newFilter;
              //   }
              // }
							
              const [metricsResult, metricsIndex] = findResultAndLocation(metrics, findObj);
							
              if (metricsResult) {
                const {accuracy, unit} = getOldChartDisplayConfig(metricsResult, chartType);
                if (query_mode === ChartCalculateMode.RawColumn && response.chart_type === ChartType.Table) {
                  newColumnRawColumns[metricsIndex] = {...item, accuracy, unit};
                } else {
                  newColumnMetrics[metricsIndex] = {...item, accuracy, unit, aggregate: metricsResult.aggregate};
                }
              }

              const [groupByResult, groupByIndex] = findResultAndLocation(group_by, findObj);
            
              if (groupByResult) {
                if (response.chart_type === ChartType.Trend) {
                  newColumnX[groupByIndex] = {...item};
                }
                // 数字维度趋势卡片从group_by里拆出 x轴(第一个)和展示维度
                else if(response.chart_type === ChartType.DimNumberTrend){
                  
                  if (groupByIndex !== 0) {
                    newColumnShowDimensions[groupByIndex] = {
                      ...item,
                    };
                  } else {
                    newColumnX[groupByIndex] = {...item};
                  }
  
                  // 存入维度映射表数据
                  newCustomDimensionMap.data = dimension || [];
                  
                } else{
                  const {accuracy, unit} = getOldChartDisplayConfig(groupByResult, chartType);
                  newColumnDimensions[groupByIndex] = {
                    ...item,
                    accuracy,
                    unit,
                  };
                }       
              }
							
            });
          });
        });
				
        // 解析自定义筛选项（与 getTableInfo 早灌同一归一化，避免进页 B1 与详情解析不一致）
        const customColumns = normalizeCustomColumnsFromFilter(custom_columns);
  
        function findCustomResultAndLocation<T>(source: T[],  item: CustomColumn): [T | undefined, number] {
          
          let location = -1;
          const result: T | undefined = source?.find(
            (filterItem, index: number) => {
              const temp = (filterItem.id === item.id) || (filterItem.column === item.name_en); // 为了兼容旧数据的判断 (filterItem.column === customColumn.name_en)
              temp && (location = index);
              return temp;
            }
          );
          return [result, location];
        }
        
        customColumns.forEach((customColumn: CustomColumn) => {
          
          const [filtersResult, filterIndex] = findCustomResultAndLocation(filters?.filters, customColumn);
          const [inheritFilter] = findCustomResultAndLocation(inheritFilters, customColumn);
          const isSameType = customColumn?.column_type === inheritFilter?.column_type;

          if (filtersResult) {
            let newFilter: IColumnInfo = {
              ...convertCustomColumnToColumnRawInfo(customColumn),
              operation: filtersResult.operation,
              column_value: filtersResult.value,
              from: filtersResult?.last_day,
              compareTimes: filtersResult?.compare_time,
              quickValue: filtersResult?.quick_value,
            };
            newFilter = {
              ...newFilter,
              operation: DBColumnType.getFilterOperation(newFilter),
              column_value: DBColumnType.getFilterValues(newFilter),
            };
            
            // 字符串筛选器自定义选项
            !isEmpty(filtersResult.customValues) && (newFilter.customValues = filtersResult.customValues);
            
            // 先继承探索页面传递的筛选器
            Object.assign(newFilter, isSameType ? inheritFilter : {});
            
            // 更正全选标识
            newFilter.isAll = newFilter?.column_value.length < 1;
            // 更正选中数量
            if (newFilter.column_type === 'date') {
              newFilter.lastCount = filtersResult.lastCount;
              newFilter.to_date_format = filtersResult.to_date_format;
              newFilter.dateType = filtersResult.dateType;
              newFilter.from = filtersResult.last_day;
              // 动态日期时间, 计算接口上传时, 需要根据column_text的内容传递value
              newFilter.column_text = DBColumnType.getFilterText(newFilter, true);
            }
            newColumnFilters[filterIndex] = newFilter;
          } else if (inheritFilter) {
            isSameType && newInheritFilters.push({...convertCustomColumnToColumnRawInfo(customColumn), ...inheritFilter});
          }
					
          if (filter_compares?.[0]) {
            
            const [filterCompareResult, filterCompareIndex] = findCustomResultAndLocation(filter_compares[0].filters, customColumn);
            
            if (filterCompareResult) {
              let newFilter: IColumnInfo = {
                ...convertCustomColumnToColumnRawInfo(customColumn),
                operation: filterCompareResult.operation,
                column_value: filterCompareResult.value,
              };
              newFilter = {
                ...newFilter,
                operation: DBColumnType.getFilterOperation(newFilter),
                column_value: DBColumnType.getFilterValues(newFilter),
              };
              // 字符串筛选器自定义选项
              !isEmpty(filterCompareResult.customValues) && (newFilter.customValues = filterCompareResult.customValues);
              
              if (newFilter.column_type === 'date') {
                newFilter.lastCount = filterCompareResult.lastCount;
                newFilter.to_date_format = filterCompareResult.to_date_format;
                newFilter.dateType = filterCompareResult.dateType;
                newFilter.from = filterCompareResult.last_day;
                // 动态日期时间, 计算接口上传时, 需要根据column_text的内容传递value
                newFilter.column_text = DBColumnType.getFilterText(newFilter, true);
              }
              newColumnFilterCompares[filterCompareIndex] = newFilter;
            }
          }
					
          const [metricsResult, metricsIndex] = findCustomResultAndLocation(metrics, customColumn);
          if (metricsResult) {
            const {accuracy, unit} = getOldChartDisplayConfig(metricsResult, chartType);
            if (query_mode === ChartCalculateMode.RawColumn && response.chart_type === ChartType.Table) {
              newColumnRawColumns[metricsIndex] = {
                ...convertCustomColumnToColumnRawInfo(customColumn),
                accuracy,
                unit
              };
            } else {
              newColumnMetrics[metricsIndex] = {
                ...convertCustomColumnToColumnRawInfo(customColumn),
                aggregate: metricsResult.aggregate,
                accuracy,
                unit
              };
            }
          }

          const [groupByResult, groupByIndex] = findCustomResultAndLocation(group_by, customColumn);
          if (groupByResult) {
            if (response.chart_type === ChartType.Trend) {
              newColumnX[groupByIndex] = {...convertCustomColumnToColumnRawInfo(customColumn)};
            } else if(response.chart_type === ChartType.DimNumberTrend){ // 数字维度趋势卡片从group_by里拆出 x轴(第一个)和展示维度
              if (groupByIndex !== 0) {
                newColumnShowDimensions[groupByIndex] = {
                  ...convertCustomColumnToColumnRawInfo(customColumn),
                };
              } else {
                newColumnX[groupByIndex] = {...convertCustomColumnToColumnRawInfo(customColumn)};
              }

            }else{
              const {accuracy, unit} = getOldChartDisplayConfig(groupByResult, chartType);
              newColumnDimensions[groupByIndex] = {
                ...convertCustomColumnToColumnRawInfo(customColumn),
                accuracy,
                unit,
              };                    
            }       
          }
        });
        // 从报表探索过来时, 以 inheritUserFilter 为准
        // 如果 inheritUserFilter 无值, 则以user_filter为准
        const userFilter = inheritUserFilter ?? user_filter;
        
        const newPayload = {
          task_id,
          chartType: SwitchChartTypes?.includes(response?.chart_type) ? (response?.chart_type || ChartType.Table) : ChartType.Table,
          statisticalMetrics: response?.statistical_metrics,
          condition,
          customColumns,
          saveObj: {
            name: response.name,
            nameCn: response.name_cn,
            label: response.label,
            business_type: response.business_type,
            type: response.type,
          },
          columnFilters: [
            ...columnFilters,
            ...newColumnFilters.filter(
              (item) => !columnFilters.find((filterItem: IColumnInfo) => filterItem.column_name === item?.column_name),
            ),
            ...newInheritFilters,
          ],
          columnCompares: [
            ...columnCompares,
            ...newColumnFilterCompares.filter(
              (item) => !columnCompares.find((filterItem: IColumnInfo) => filterItem.column_name === item?.column_name),
            ),
          ],
          columnMetrics: [
            ...columnMetrics,
            ...newColumnMetrics.filter(
              (item) => !columnFilters.find((metricItem: IColumnInfo) => metricItem.column_name === item?.column_name),
            ),
          ],
          columnDimensions: [
            ...columnDimensions,
            ...newColumnDimensions.filter(
              (item) =>
                !columnDimensions.find(
                  (dimensionsItem: IColumnInfo) => dimensionsItem.column_name === item?.column_name,
                ),
            ),
          ],
          columnX: [
            ...columnX,
            ...newColumnX.filter(
              (item) =>
                !columnX.find(
                  (dimensionsItem: IColumnInfo) => dimensionsItem.column_name === item?.column_name,
                ),
            ),
          ],
          columnShowDimensions: [
            ...columnShowDimensions,
            ...newColumnShowDimensions.filter(
              (item) =>
                !columnShowDimensions.find(
                  (dimensionsItem: IColumnInfo) => dimensionsItem.column_name === item?.column_name,
                ),
            ),
          ],
          customDimensionMap: {
            ...newCustomDimensionMap
          },
          columnRawColumns: [
            ...columnRawColumns,
            ...newColumnRawColumns.filter(
              (item) =>
                !columnRawColumns.find(
                  (dimensionsItem: IColumnInfo) => dimensionsItem.column_name === item?.column_name,
                ),
            ),
          ],
          mode: query_mode,
          forceAssignEmptyHashAnalysis: false,
          calculateHashForAnalysis: true,
          hasUserFilters: userFilter?.controls?.length > 0,
          columnUserFilters: userFilter?.controls,
        };
        // 过滤
        yield put({
          type: 'save',
          payload: newPayload,
        });
				
				
        // yield put({
        //   type: 'initColunmOptions',
        //   payload: {
        //     columnFilters: newColumnFilters,
        //     sortableKey: 'columnFilters',
        //   },
        // });
        //
        // yield put({
        //   type: 'initColunmOptions',
        //   payload: {
        //     columnFilters: newColumnFilterCompares,
        //     sortableKey: 'columnCompares',
        //   },
        // });
				
        if (callback) {
          const {isCheckSuccess} = ((newPayload.chartType === ChartType.Table) && (newPayload.mode === ChartCalculateMode.RawColumn)) ? checkRawColumnModeConditions(newPayload.chartType, {columnFilters: newPayload.columnFilters, columnRawColumns: newPayload.columnRawColumns}, true) : checkAllConditions(newPayload.chartType, newPayload, true);
          // 符合计算条件的, 才发起自动计算请求
          isCheckSuccess && callback(save_column_infos.chartsCfgSave);
        }
      } catch (e) {
        console.error(e);
      }
    },
		
    // 初始化下拉选择项
    * initColunmOptions({payload}: { payload: any; }, {put}: { put: any; }): any {
      try {
        const {columnFilters, sortableKey}: { columnFilters: IColumnInfo[]; sortableKey: string; } = payload;
				
        const searchOptionsList: IColumnInfo[] = [];
        const getOptionsList: IColumnInfo[] = [];
        (columnFilters || []).forEach((item: IColumnInfo) => {
          if (includeOperation(item.operation)) {
            if (item.column_value && item.column_value.length > 0) {
              searchOptionsList.push(item);
            }
            getOptionsList.push(item);
          }
        });
        // for (const item of searchOptionsList) {
        //   yield put({
        //     type: 'searchColumnOptionsList',
        //     payload: {
        //       sortableKey,
        //       columnInfo: item,
        //       query: item.column_value,
        //       type: 1,
        //       isDetail: true,
        //     },
        //   });
        // }
				
        for (const item of getOptionsList) {
          yield put({
            type: 'getColumnOptionsList',
            payload: {
              sortableKey,
              newOptionsObj: item,
            },
          });
        }
      } catch (e) {
        console.error(e);
      }
    },
		
    * setUserPackages({payload, callback}: { payload: any; callback: Function; }, {
      put,
      select
    }: { put: any; select: any; }): any {
      try {
        const {
          gameCode,
          packageUuids = [],
          userPackages = [],
        }: { gameCode: string; packageUuids: number[]; userPackages: INumberPackage[]; } = payload;
        const {userPackages: oldUserPackages} = yield select(
          (state: { [x: string]: any; }) => state[namespace],
        );
				
        let newUserPackages: INumberPackage[] = [];
        if (packageUuids.length > 0) {
          const packageRsp = yield getNumberPackageDetail({
            game_code: gameCode,
            package_uuid: String(packageUuids[0]),
          });
          newUserPackages.push({
            ...packageRsp,
            relate_column: oldUserPackages?.[0]?.relate_column || []
          });
        } else {
          newUserPackages = userPackages;
        }
        yield put({
          type: 'save',
          payload: {
            userPackages: newUserPackages,
          },
        });
				
        if (callback) {
          callback();
        }
      } catch (e) {
        console.error(e);
      }
    },
		
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    * clearUserPackages({payload}: { payload: any; }, {put}: { put: any; }): any {
      yield put({
        type: 'save',
        payload: {
          userPackages: [],
        },
      });
    },
		
    * clearQuery({payload={}, callback}: { payload: any; callback: Function; }, {put}: { put: any; select: any; }): any {
      yield put({
        type: 'save',
        payload: {
          // task_id: '',
          columnFilters: [],
          columnUserFilters: [],
          columnMetrics: [],
          columnDimensions: [],
          columnX: [],
          columnShowDimensions: [],
          customDimensionMap: { options: [], data: [], loading: true},
          columnRawColumns: [],
          table: undefined,
          chartData: {},
          hadAnalysed: false,
          hasUserFilters: false,
          calculateHashForAnalysis: true,
          rowLimit: 5000,
          // newFlag: undefined,
          ...payload
        },
      });
			
      if (callback) {
        callback();
      }
    },
    // 编辑sql虚拟表改变数据源的时候清空数据
    * sqlSelectDataSetClear({callback}: { payload: any; callback: Function; }, {put}: { put: any; select: any; }): any {
      yield put({
        type: 'save',
        payload: {
          // columnFilters: [],
          columnCompares: [],
          columnMetrics: [],
          columnDimensions: [],
          columnX: [],
          columnShowDimensions: [],
          customDimensionMap: { options: [], data: [], loading: true},
          table: undefined,
          chartData: {},
          hadAnalysed: false,
        },
      });
			
      if (callback) {
        callback();
      }
    },
		
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    * clearTableInfo({payload}: { payload: any; }, {put, select}: { put: any; select: any; }): any {
      const {
        customColumns,
        chartType,
        save_column_infos,
      }: {
        customColumns: CustomColumn[];
        chartType: string;
        save_column_infos: unknown;
      } = yield select((state: { [x: string]: any; }) => state[namespace]);

      // 清表后 schema 已确认（可为空）：允许 B1 回退失效的字段排序
      applyDatasetFieldSortResetIfUnresolved([], customColumns || [], true);
      const nextSaveColumnInfos = pruneSaveColumnInfosOnDatasetFieldSortUnresolved(
        save_column_infos,
        chartType as TChartType,
        [],
        customColumns || [],
        true,
      );

      yield put({
        type: 'save',
        payload: {
          dataTables: [],
          dataTablesSchemaReady: true,
          save_column_infos: nextSaveColumnInfos,
        },
      });
    },
		
    * filterConditionByTable({payload}: { payload: any; }, {put, select}: { put: any; select: any; }): any {
      const {
        table_ids,
        excludeTable = false,
        isClearRelations = false,
        forceAssignEmptyHashAnalysis = false,
        tables=[],
      }: { table_ids: any[]; excludeTable: boolean; isClearRelations: boolean; tableInfo: any; } = payload;
      const {
        columnFilters,
        columnCompares,
        columnMetrics,
        columnDimensions,
        columnX,
        columnShowDimensions,
        customDimensionMap,
        save_column_infos: saveColumnInfos
      }: {
        dataTables: IGetTableInfoDto[];
        dataTableJoinRelations: ITableJoinRelation[];
        isOpenSelfJoin: boolean;
        columnFilters: IColumnInfo[];
        columnCompares: IColumnInfo[];
        columnMetrics: IColumnInfo[];
        columnDimensions: IColumnInfo[];
        columnX: IColumnInfo[];
        columnShowDimensions: IColumnInfo[];
        customDimensionMap: DimensionMapInfo;
        save_column_infos: any;
      } = yield select((state: { [x: string]: any; }) => state[namespace]);
      // const isSqlSchemaAggregationList :  string[] = [];

      // 将删除的数据表数据过滤掉
      const filterData = (arr: any[], type?: string) =>
        arr?.filter?.((item) => {
          if (item?.column_custom) return true; // 自定义字段保留, 不删除
          // let result;
          // 1. 先判断 替换前后数据表id相同的情况下, 筛选字段 和 表字段 是相同原始名称和类型时, 保留
          // 适配场景:
          // * 三张表关联时t0 t1 t2, 只删除了t1, t2变成了t1时, 需要保留操作区字段, 并更新table_code
          // *
          // 星哥说先不做这么复杂的场景
          // tables.forEach((table, index) => {
          //   if (table?.table_id === item?.table_id) {
          //     // result 为true, 说明在数据表中, 存在右侧操作区的字段
          //     result = table?.column_list?.find(column =>
          //       ((item?.column || item?.column_name) === column?.name && item?.column_type === column?.type)
          //     );
          //     result && (item.table_code = `t${index}`);
          //
          //     console.log("** tableCodeNumber", {item, tableCodeNumber, list: tables[tableCodeNumber]?.column_list, result});
          //   }
          // });
          //
          // if (result) return true;
          
          // 说明 操作字段所在的数据表已经被删除, 按照table_code相同的条件下再次查找.
          // 2. 判断 tableCode相同的情况下, 筛选字段 和 表字段是否相同原始名称和类型
          // 这个是为了 用户替换了 和原来数据表字段都相同, 但id不同的数据表 场景
          const tableCodeNumber = Number(item?.table_code?.[1] ?? 0);
          const result = tables[tableCodeNumber]?.column_list?.find(column =>
            ((item?.column || item?.column_name) === column?.name && item?.column_type === column?.type)
          );
          
          if (result) {
            item.table_id = tables[tableCodeNumber].table_id; // 需要更新原来字段的table_id
            item.id = `${item.table_id}.${(item?.column || item?.column_name)}`;
            item.column_display = result.display_name || result.name;
            item.column_display_en = result.display_name_en || result.name;
          } else {
            // 如果 column 字段不存在 操作区的字段里了, 那么在 表格类型的图表配置中删除对应的字段

            // 1. 分类维度中 字段设置url
            // 2. 统计指标和分类维度 的Description设置
            const tableSetting = saveColumnInfos?.chartsCfgSave?.Table?.customizeSetting?.table;
            const description = tableSetting?.description;
            const field_setting = tableSetting?.field_setting;
            if (description) {
              delete description[`${item.table_code}_${(item?.column || item?.column_name)}_${type === 'metric' ? 0 : (type === 'dimension' ? 1 : '')}`];
            }
            if (field_setting) {
              delete field_setting[`${item.table_code}_${(item?.column || item?.column_name)}`];
            }
          }
          
          return result;
        }) || [];
      const newColumnFilters = [
        ...filterData(columnFilters)
      ];
			
      const newColumnCompares = [
        ...filterData(columnCompares)
      ];
			
      const newColumnMetrics = [
        ...filterData(columnMetrics, 'metric')
      ];
			
      const newColumnDimensions = [
        ...filterData(columnDimensions, 'dimension')
      ];
      const newColumnX = [
        ...filterData(columnX)
      ];

      let newCustomDimensionMap = customDimensionMap;
      const newColumnShowDimensions = [
        ...filterData(columnShowDimensions)
      ];
      // 展示维度数据被过滤掉了，映射表也需要清楚
      if(newColumnShowDimensions.length === 0){
        newCustomDimensionMap = { options: [], data: [], loading: true};
      }

      yield put({
        type: 'save',
        payload: {
          columnFilters: newColumnFilters,
          columnCompares: newColumnCompares,
          columnMetrics: newColumnMetrics,
          columnDimensions: newColumnDimensions,
          columnX: newColumnX,
          columnShowDimensions: newColumnShowDimensions,
          customDimensionMap: newCustomDimensionMap,
          columnUserFilters: [],
          hasUserFilters: false,
          table: undefined, // table 数据
          chartData: {}, // 折线图和柱状图数据
          hadAnalysed: false,
          save_column_infos: omit(saveColumnInfos, excludeTable && isClearRelations && table_ids),
          forceAssignEmptyHashAnalysis
        },
      });
    },
		
    * deleteConditionByCustomColumn({payload}: { payload: any; }, {put, select}: { put: any; select: any; }): any {
      const {deleteCustomColumn, customColumns}: {deleteCustomColumn: CustomColumn; customColumns: CustomColumn[]; } = payload;

      const deleteCustomColumnId = deleteCustomColumn?.id;
      const {
        columnFilters,
        columnCompares,
        columnMetrics,
        columnDimensions,
        columnX,
        columnShowDimensions,
        customDimensionMap,
        columnRawColumns,
        columnUserFilters,
        save_column_infos: saveColumnInfos,
        chartType
      }: {
        columnFilters: IColumnInfo[];
        columnCompares: IColumnInfo[];
        columnMetrics: IColumnInfo[];
        columnDimensions: IColumnInfo[];
        columnX: IColumnInfo[];
        columnShowDimensions: IColumnInfo[];
        customDimensionMap: DimensionMapInfo;
        columnRawColumns: IColumnInfo[];
        columnUserFilters: IColumnInfo[];
        save_column_infos: any;
        chartType: string;
      } = yield select((state: { [x: string]: any; }) => state[namespace]);

      const { removedDimensions, removedMetrics } =
        collectRemovedSnapshotsForCustomColumn({
          deleteCustomColumnId,
          columnDimensions,
          columnMetrics,
          columnX,
          columnShowDimensions,
        });
      const newColumnFilters = columnFilters
        .filter((item: IColumnInfo) => deleteCustomColumnId !== item.id)
        .map((item) => item);
			
      const newColumnCompares = columnCompares
        .filter((item: IColumnInfo) => deleteCustomColumnId !== item.id)
        .map((item) => item);
			
      const newColumnMetrics = columnMetrics
        .filter((item: IColumnInfo) => deleteCustomColumnId !== item.id)
        .map((item) => item);
			
      const newColumnDimensions = columnDimensions
        .filter((item: IColumnInfo) => deleteCustomColumnId !== item.id)
        .map((item) => item);
      
      const newColumnX = columnX
        .filter((item: IColumnInfo) => deleteCustomColumnId !== item.id)
        .map((item) => item);
      
      let newCustomDimensionMap = customDimensionMap;
      const newColumnShowDimensions = columnShowDimensions
        .filter((item: IColumnInfo) => {
          // 需清除维度映射表数据
          if(deleteCustomColumnId === item.id){
            newCustomDimensionMap = { options: [], data: [], loading: true};
          }
          return deleteCustomColumnId !== item.id;
        })
        .map((item) => item);

      const newColumnRawColumns = columnRawColumns
        .filter((item: IColumnInfo) => deleteCustomColumnId !== item.id)
        .map((item) => item);

      const nextQb = {
        columnDimensions: newColumnDimensions,
        columnMetrics: newColumnMetrics,
        columnFilters: newColumnFilters,
        columnUserFilters: columnUserFilters ?? [],
      };
      const removalChangeSet = buildRemovalChangeSet({
        removedDimensions,
        removedMetrics,
        nextSnapshot: {
          ...nextQb,
          hasDateDimension: detectDateDimensionsInList(newColumnDimensions),
        },
        prevDimCount: columnDimensions.length,
        prevMetCount: columnMetrics.length,
        prevHasDateDimension: detectDateDimensionsInList(columnDimensions),
      });

      let nextSaveColumnInfos = saveColumnInfos;
      if (removalChangeSet) {
        applyCustomColumnDeleteOnQB(removalChangeSet, nextQb);
        nextSaveColumnInfos = pruneSaveColumnInfosOnCustomColumnDelete(
          saveColumnInfos,
          chartType as TChartType,
          removalChangeSet,
          nextQb,
        );
      }

      // A1：字段排序引用该自定义字段时，base+draft+save 回默认（即使未进 QB）
      const deletedNames = [
        deleteCustomColumn?.name_en,
        deleteCustomColumn?.name_cn,
      ].filter(Boolean) as string[];
      applyDatasetFieldSortResetOnCustomColumnDelete(deletedNames);
      nextSaveColumnInfos = pruneSaveColumnInfosOnDatasetFieldSortCustomDelete(
        nextSaveColumnInfos,
        chartType as TChartType,
        deletedNames,
      );

      yield put({
        type: 'save',
        payload: {
          columnFilters: newColumnFilters,
          columnCompares: newColumnCompares,
          columnMetrics: newColumnMetrics,
          columnDimensions: newColumnDimensions,
          columnX: newColumnX,
          columnShowDimensions: newColumnShowDimensions,
          customDimensionMap: newCustomDimensionMap,
          columnRawColumns: newColumnRawColumns,
          customColumns,
          calculateHashForAnalysis:
						(newColumnFilters.length === columnFilters.length)
						&& (newColumnCompares.length === columnCompares.length)
						&& (newColumnMetrics.length === columnMetrics.length)
						&& (newColumnDimensions.length === columnDimensions.length)
						&& (newColumnX.length === columnX.length)
            && (newColumnShowDimensions.length === columnShowDimensions.length),
          save_column_infos: nextSaveColumnInfos,
        },
      });
    },
		
    * resetState({payload}: { payload: any; }, {put}: { put: any; }): any {
      yield put({
        type: 'save',
        payload: {...initState(), resetState: true, gameCode: payload?.gameCode ?? ''},
      });
    },
		
    * updateTableField({payload}: { payload: any; callback: Function; }, {
      put,
      select
    }: { put: any; select: any; }): any {
      try {
        const {
          updateColumn,
          column,
          table_id,
        }: { updateColumn: UpdateColumnInfo; column: IColumnInfo; table_id: string; isEn: boolean; calculateHashForSave: boolean; } = payload;
				
        // 记录字段重命名前的中英文展示名称, 用于后续判断是否需要更新排序配置
        const prevColumnDisplay = column.column_display;
        const prevColumnDisplayEn = column.column_display_en;

        updateColumn.column_name = column.column_name;
        // 判断重命名更新的是中文还是英文 (右侧字段的显示名称属性是column_display_en column_display; 左侧字段显示名称属性是 display_name_en display_name)
  
        updateColumn.column_display = updateColumn.display_name || column.column_display;
        updateColumn.display_name = updateColumn.column_display;
        
        // 需求变更: 页面无论是中文还是英文状态, 字段重命名后, 名称的中英文都是一样的, 字段重命名不区分中英文
        // updateColumn.column_display_en = (isEn && updateColumn.display_name) || column.column_display_en;
        updateColumn.column_display_en = updateColumn.display_name || column.column_display;
        updateColumn.display_name_en = updateColumn.column_display;
        // updateColumn.column_display = (!isEn && updateColumn.display_name) || column.column_display;
        
				
        if (updateColumn.attribute != undefined) { // 如果用户转换了指标/维度
          (updateColumn.attribute == 1) && (updateColumn.flag = 1); // 从指标转换成维度时, 汇总值默认不包含
          updateColumn.isCommonDimension = false;
          updateColumn.is_common_dimension = false;
          updateColumn.is_dimension = updateColumn.attribute == 1;
          updateColumn.is_metric = updateColumn.attribute == 2;
        }

        const { chartType }: { chartType: TChartType; } = yield select(
          (state: { [x: string]: any; }) => state[namespace],
        );

        // 字段类型切换：小数位与展示格式回到该类型默认值，不保留上一类型用户配置
        if (
          updateColumn.column_type &&
          updateColumn.column_type !== column.column_type
        ) {
          const defaultAccuracy = (
            FieldTypeDefaultAccuracy as Record<string, number>
          )[updateColumn.column_type];
          updateColumn.accuracy =
            defaultAccuracy !== undefined ? defaultAccuracy : 0;
          updateColumn.unit = getDefaultNumberUnit({
            fieldType: updateColumn.column_type as any,
            chartType,
          });
        }
				
        const newColumn = {...column, ...updateColumn};
        updateColumn.accuracy = newColumn.accuracy;
        updateColumn.flag = newColumn.flag;
        updateColumn.column_type = newColumn.column_type;
        updateColumn.dimension_setting = newColumn.dimension_setting;
        // 如果用户更改了维度值或者汇总值, 需要重新计算是否是公共维度
        updateColumn.isCommonDimension = (!newColumn.is_metric) && (newColumn.dimension_setting === 'system') && (newColumn.flag == 0);
        updateColumn.is_common_dimension = updateColumn.isCommonDimension;
        updateColumn.is_dimension = !newColumn.is_metric;
        updateColumn.is_metric = newColumn.is_metric;
				
				
        const {
          dataTables,
          columnFilters,
          columnCompares,
          columnMetrics,
          columnDimensions,
          columnX,
          columnShowDimensions,
          columnRawColumns,
          save_column_infos = {},
        }: {
          dataTables: IGetTableInfoDto[];
          dataTableJoinRelations: ITableJoinRelation[];
          isOpenSelfJoin: boolean;
          columnFilters: IColumnInfo[];
          columnCompares: IColumnInfo[];
          columnMetrics: IColumnInfo[];
          columnDimensions: IColumnInfo[];
          columnX: IColumnInfo[];
          columnShowDimensions: IColumnInfo[];
          columnRawColumns: IColumnInfo[];
          save_column_infos: any;
        } = yield select((state: { [x: string]: any; }) => state[namespace]);
				
        /** 更新左侧 表字段信息 */
        const updateTable: any = dataTables.find((table) => table.table_id === table_id) || {};
        // 更新字段的方法
        const someHandle = (oldColumn: TEnrichedTableColumn, i: number, arr: any[]) => {
          const oldName = oldColumn.column_name || oldColumn.name;
          if (oldName === updateColumn.column_name) {
            arr[i] = {...oldColumn, ...updateColumn};
            return true;
          }
          return false;
        };
        // 找到 维度, 指标的字段数组下标
        const index = column.is_metric ? 1 : 0;
        if (updateColumn.attribute != undefined) {
          // 转换指标, 维度 ( 根据 维度值和是否包含汇总值, 判断是不是公共维度) 在原来的数组删除数据, 在新的分类数组中新增数据
          // 删除原来分类数组中的数据
          updateTable.column_class_list[index].columns = updateTable.column_class_list[index].columns.filter(item => (item.column_name !== column.column_name));
          // 找到新的分类数组下标
          const newIndex = updateColumn.attribute == 1 ? 0 : 1;
          // 在新的分类数组中添加新的字段
          updateTable.column_class_list[newIndex].columns.unshift({...column, ...updateColumn});
					
        } else {
          updateTable.column_class_list[index]?.columns?.some(someHandle);
        }
        updateTable.column_list?.some(someHandle);
				
        /** 更新右侧操作拖拽后的字段 */
        const updateFilter = columnFilters?.find(filter => (filter.id === column.id));
        const updateCompare = columnCompares?.find(filter => (filter.id === column.id));
        const updateMetric = columnMetrics?.find(filter => (filter.id === column.id));
        const updateDimension = columnDimensions?.find(filter => (filter.id === column.id));
        const updateX = columnX?.find(filter => (filter.id === column.id));
        const updateShowDimensions = columnShowDimensions?.find(filter => (filter.id === column.id));
        const updateRawColumn = columnRawColumns?.find(filter => (filter.id === column.id));
				
        [updateFilter, updateCompare, updateMetric, updateDimension, updateShowDimensions, updateRawColumn, updateX].forEach((item: IColumnInfo | undefined) => {
          if (!item) return;
          Object.assign(item, updateColumn);
        });
				
        // 需要上传给后端进行保存的更改的字段信息
        const newColumnsMap = save_column_infos?.[table_id] || {};
        newColumnsMap[updateColumn.column_name] = Object.assign(newColumnsMap[updateColumn.column_name] || {}, updateColumn);
        save_column_infos[table_id] = newColumnsMap;
        
        // 针对表格类型下的图表配置的修改
        if (typeof save_column_infos?.chartsCfgSave?.Table?.customizeSetting?.table === 'object') {
          const { column_percentage=[] } = save_column_infos.chartsCfgSave.Table.customizeSetting.table;
          if ((updateMetric || updateRawColumn) && !updateColumn.is_metric) { // 字段不是指标时, 查看一下图表配置中是否有 指标的配置, 需要删除
            const tableCode = updateMetric?.table_code || updateRawColumn?.table_code || 't0';
            const name = updateColumn.column_name;
            
            save_column_infos.chartsCfgSave.Table.customizeSetting.table.column_percentage = column_percentage.filter((item) => (item.metric !== name) || (item.table_code !== tableCode));
          }
        }
        
        yield put({
          type: 'save',
          payload: {
            dataTables: [...dataTables],
            columnFilters: [...columnFilters],
            columnCompares: [...columnCompares],
            columnMetrics: [...columnMetrics],
            columnDimensions: [...columnDimensions],
            columnXDimensions: [...columnX],
            columnShowDimensions: [...columnShowDimensions],
            columnRawColumns: [...columnRawColumns],
            save_column_infos,
          }
        });

        // 只有在字段中/英文展示名称发生变化时, 才需要同步更新表格排序配置
        const hasColumnDisplayChanged = (
          updateColumn.column_display !== prevColumnDisplay
          || updateColumn.column_display_en !== prevColumnDisplayEn
        );

        // 列显示名称变更 — ChartSettingsV2 内部监听自行处理
      } catch (e) {
        console.error("更新字段失败", e);
      }

    },
  
    // 更新或新建自定义字段
    * updateCustomColumn({payload}: { payload: { updateColumn: CustomColumn; }; callback: Function; }, {
      put,
      select
    }: { put: any; select: any; }): any {
      try {
        const {
          updateColumn,
        } = payload;
        const {
          chartType,
          customColumns,
          columnFilters,
          columnCompares,
          columnMetrics,
          columnDimensions,
          columnX,
          columnShowDimensions,
          columnRawColumns,
          save_column_infos: saveColumnInfos,
        }: {
          chartType:string;
          customColumns: CustomColumn[];
          columnFilters: IColumnInfo[];
          columnCompares: IColumnInfo[];
          columnMetrics: IColumnInfo[];
          columnDimensions: IColumnInfo[];
          columnShowDimensions: IColumnInfo[];
          columnRawColumns: IColumnInfo[];
          columnX: IColumnInfo[];
          save_column_infos: unknown;
        } = yield select((state: { [x: string]: any; }) => state[namespace]);

        
        // 更新自定义字段的状态
        const newCustomColumns: CustomColumn[] = [...(customColumns || [])];
        const columnIndex = (customColumns || []).findIndex((item) => item.id === updateColumn.id);
        const oldColumnName = newCustomColumns[columnIndex]?.name_cn;
        
        if (columnIndex < 0) {
          newCustomColumns.push(updateColumn);
        } else {
          Object.assign(newCustomColumns[columnIndex], updateColumn);
        }
        
        
      
        /** 更新右侧操作拖拽后的字段 */
        const updateFilter = columnFilters?.find(filter => (filter.id === updateColumn.id));
        const updateCompare = columnCompares?.find(filter => (filter.id === updateColumn.id));
        const updateMetric = columnMetrics?.find(filter => (filter.id === updateColumn.id));
        const updateDimension = columnDimensions?.find(filter => (filter.id === updateColumn.id));
        const updateX = columnX?.find(filter => (filter.id === updateColumn.id));
        const updateShowDimension = columnShowDimensions?.find(filter => (filter.id === updateColumn.id));
        const updateRawColumn = columnRawColumns?.find(filter => (filter.id === updateColumn.id));
        
        [updateFilter, updateCompare, updateMetric, updateDimension, updateShowDimension, updateRawColumn, updateX].forEach((item: IColumnInfo | undefined) => {
          if (!item) return;
          // 自定义字段更新 字段类型
          if (item.column_type !== updateColumn.column_type) {
            // 更新后是日期类型
            if (updateColumn.column_type === 'date') {
              
              item.lastCount = 7;
              item.dateType = 'day';
              item.date_format = 'YYYY-MM-DD';
              item.column_value = ['7-day'];
              item.operation = 'recent';
              item.column_text = '';
              item.from = '1';
            } else {
              item.column_value = [];
              item.column_text = '';
              item.operation = updateColumn.column_type === 'float' ? 'greater' : 'include';
              const newAggregate = updateColumn.column_type === 'string' ? chartType === ChartType.DimNumberTrend ? 'distinct' :  'max' : 'sum';
              item.aggregate = item.is_aggregate ? '' : newAggregate;
            }
            
          }
          // 小数位数
          item.accuracy = (updateColumn.accuracy !== undefined) ? updateColumn.accuracy : item.accuracy;
          // 数据展示bmk还是千分隔符
          item.unit = (updateColumn.unit !== undefined) ? updateColumn.unit : item.unit;
          item.column_type = updateColumn.column_type || item.column_type;
          
          // 更新 名称
          item.column_display = updateColumn.name_cn || item.column_display;
          item.column_display_en = updateColumn.name_en || item.column_display_en;
          item.column_name = updateColumn.name_cn || item.column_name;

          // 更新 备注
          item.column_remark = updateColumn.remark || item.column_remark;

          // 更新 转换后的日期格式
          item.to_date_format = updateColumn.to_date_format || item.to_date_format;
          item.date_format = updateColumn.date_format || item.date_format;

          // 字符串类型, 是否为全部全部
          updateColumn.isAll ?? (item.isAll = updateColumn.isAll);
          item.sql_expression = updateColumn.sql_expression;
        });
        
        /**
         * 自定义字段改名：同步迁移图表设置（Context + save_column_infos）
         *
         * name_cn 为唯一身份。已进 QB 时迁维/指相关配置；未进 QB 时仍迁
         * DatasetFieldSort.chartSorts（字段排序可不在操作区）。
         * 必须在「原地改 column_name」之后、put 之前执行。
         *
         * @see applyCustomColumnRenameOnQB
         * @see migrateSaveColumnInfosOnCustomColumnRename
         */
        let nextSaveColumnInfos = saveColumnInfos;
        const qbRenameTarget = updateDimension || updateMetric;
        if (
          updateColumn.name_cn &&
          oldColumnName &&
          oldColumnName !== updateColumn.name_cn
        ) {
          const renameParams = {
            tableCode: qbRenameTarget?.table_code || 't0',
            tableId: qbRenameTarget?.table_id || '',
            oldColumnName,
            newColumnName: updateColumn.name_cn,
            builderType: (updateDimension
              ? 'dimension'
              : updateMetric
                ? 'metric'
                : 'dimension') as 'dimension' | 'metric',
            columnDisplay: updateColumn.name_cn,
            columnDisplayEn: updateColumn.name_en,
          };
          nextSaveColumnInfos = migrateSaveColumnInfosOnCustomColumnRename(
            saveColumnInfos,
            chartType as TChartType,
            renameParams,
          );
          applyCustomColumnRenameOnQB(renameParams);
        }

        yield put({
          type: 'save',
          payload: {
            columnFilters: [...columnFilters],
            columnCompares: [...columnCompares],
            columnMetrics: [...columnMetrics],
            columnDimensions: [...columnDimensions],
            columnX: [...columnX],
            columnShowDimensions: [...columnShowDimensions],
            columnRawColumns: [...columnRawColumns],
            customColumns: newCustomColumns,
            ...(nextSaveColumnInfos !== saveColumnInfos
              ? { save_column_infos: nextSaveColumnInfos }
              : {}),
            // calculateHashForAnalysis: true,
          }
        });
        
      } catch (e) {
        console.error("更新字段失败", e);
      }
    
    }
    
  },
};

const buildCondition = ({
  gameCode,
  mode,
  hasUserFilters,
  columnFilters,
  columnUserFilters,
  columnCompares,
  columnMetrics,
  columnDimensions,
  columnX,
  columnShowDimensions,
  columnRawColumns,
  customDimensionMap,
  customColumns,
  chartType,
  rowLimit,
  ...other
}:{
  gameCode: string;
  mode: TChartCalculateMode;
  hasUserFilters: boolean;
  columnFilters: IColumnInfo[];
  columnUserFilters: IColumnInfo[];
  columnCompares: IColumnInfo[];
  columnMetrics: IColumnInfo[];
  columnDimensions: IColumnInfo[];
  columnX: IColumnInfo[];
  columnShowDimensions: IColumnInfo[];
  columnRawColumns: IColumnInfo[];
  customDimensionMap: DimensionMapInfo;
  customColumns: CustomColumn[];
  chartType: TChartType;
  rowLimit?: string | number;
}, isCalculate=false,
) => {
  
  // 统计指标判断
  const isRaw = chartType === ChartType.Table && mode === ChartCalculateMode.RawColumn;
  const metrics = isRaw ? columnRawColumns : columnMetrics;
  const newMetrics: MetricsItem[] = createMetricsItem(metrics, isRaw);
  
  // 分类维度
  let dimension = [];
  // 针对不同类型的图表上传分类维度逻辑
  let groupBy = columnDimensions;
  if (chartType === ChartType.DimNumberTrend) {
    groupBy = [...columnX, ...columnShowDimensions];
    dimension = customDimensionMap?.data || [];
  } else if (chartType === ChartType.Trend) {
    groupBy = columnX;
  } else if (chartType === ChartType.BigNumber || isRaw) {
    groupBy = [];
  }
  const newGroupBy: GroupBy[] = createGroupBy(groupBy);
  
  // 过滤条件
  const newFilters: FiltersItem[] = createFiltersItem(columnFilters, isCalculate);
  // 表格, 饼图, 堆叠, 折线才有 对比项过滤条件2
  let newFilterCompares = [];
  switch (chartType) {
    case ChartType.Table: {
      if (mode === ChartCalculateMode.Aggregate) {
        newFilterCompares = createFilterCompares(columnCompares, isCalculate);
      }
      break;
    }
    case ChartType.Pie:
    case ChartType.StackBar:
    case ChartType.Line: {
      newFilterCompares = createFilterCompares(columnCompares, isCalculate);
    }
  }
  const isUserFilter = hasUserFilters && allowChartTypeShowUserFilter({chartType, mode});
  const newCondition: Condition = {
    game_code: gameCode,
    dimension,
    group_by: newGroupBy,
    filters: {
      // filters: newFilters.filter((item: FiltersItem) => item.value.length > 0),
      filters: newFilters,
    },
    filter_compares: newFilterCompares?.map((analysisFilter: AnalysisFilters) => ({
      // filters: analysisFilter.filters.filter((filtersItem: FiltersItem) => filtersItem.value.length > 0),
      filters: analysisFilter.filters,
    })),
    // 用户筛选器
    ...(isUserFilter && columnUserFilters?.length > 0 ? {
      user_filter: {
        controls: columnUserFilters
      }
    } : {}),
    metrics: newMetrics,
    orderby: [],
    custom_columns: customColumns,
    rowLimit,
    query_mode: isRaw ? ChartCalculateMode.RawColumn : ChartCalculateMode.Aggregate,
    ...other
  };
  return newCondition;
};

const bulidDataPackageInfos = (userPackages: INumberPackage[]) =>
  (userPackages || []).map((item) => {
    const info: AnalysisPackageInfo = {
      package_uuid: String(item.package_uuid),
      relate_column: item.relate_column,
    };
    return info;
  });
